import { v4 as uuid } from "uuid";
import { z } from "zod";
import { EmissionFactorType } from "../emissionFactor";
// ---------------------------------------------------------------------------
// Environmental Target Type (top-level filter)
// ---------------------------------------------------------------------------
export var EnvironmentalTargetType;
(function (EnvironmentalTargetType) {
    EnvironmentalTargetType["EnergyAndFuelsAndLubricants"] = "environmental-target-type_energy-and-fuels-and-lubricants";
    EnvironmentalTargetType["WaterUsage"] = "environmental-target-type_water-usage";
    EnvironmentalTargetType["Waste"] = "environmental-target-type_waste";
    EnvironmentalTargetType["Materials"] = "environmental-target-type_materials";
    EnvironmentalTargetType["Transport"] = "environmental-target-type_transport";
})(EnvironmentalTargetType || (EnvironmentalTargetType = {}));
export const environmentalTargetTypeToString = (t) => {
    switch (t) {
        case EnvironmentalTargetType.EnergyAndFuelsAndLubricants:
            return "Energy, Fuels & Lubricants";
        case EnvironmentalTargetType.WaterUsage:
            return "Water Usage";
        case EnvironmentalTargetType.Waste:
            return "Waste";
        case EnvironmentalTargetType.Materials:
            return "Materials";
        case EnvironmentalTargetType.Transport:
            return "Transport";
        default:
            return String(t);
    }
};
// ---------------------------------------------------------------------------
// Environmental Category
// ---------------------------------------------------------------------------
export var EnvironmentalCategory;
(function (EnvironmentalCategory) {
    // Energy
    EnvironmentalCategory["EnergyElectricity"] = "environmental-category_electricity";
    EnvironmentalCategory["EnergyFuelType"] = "environmental-category_fuel-type";
    // Water
    EnvironmentalCategory["WaterPotable"] = "environmental-category_potable";
    EnvironmentalCategory["WaterNonPotable"] = "environmental-category_non-potable";
    EnvironmentalCategory["WaterReusedOnSite"] = "environmental-category_reused-on-site";
    EnvironmentalCategory["WaterDischarge"] = "environmental-category_water-discharge";
    // Waste
    EnvironmentalCategory["ConstructionDemolitionWaste"] = "environmental-category_construction-demolition-waste";
    EnvironmentalCategory["SpoilFillSoilMaterial"] = "environmental-category_spoil-fill-soil-material";
    EnvironmentalCategory["SolidLiquidMixedWaste"] = "environmental-category_solid-liquid-mixed-waste";
    EnvironmentalCategory["OfficeWaste"] = "environmental-category_office-waste";
    // Materials
    EnvironmentalCategory["MaterialsConcreteReadyMix"] = "environmental-category_materials_concrete-ready-mix";
    EnvironmentalCategory["MaterialsPrecastConcrete"] = "environmental-category_materials_precast-concrete";
    EnvironmentalCategory["MaterialsAsphalt"] = "environmental-category_materials_asphalt";
    EnvironmentalCategory["MaterialsBitumen"] = "environmental-category_materials_bitumen";
    EnvironmentalCategory["MaterialsWoodTimber"] = "environmental-category_materials_wood-timber";
    EnvironmentalCategory["MaterialsCement"] = "environmental-category_materials_cement";
    EnvironmentalCategory["MaterialsAggregates"] = "environmental-category_materials_aggregates";
    EnvironmentalCategory["MaterialsSteel"] = "environmental-category_materials_steel";
    EnvironmentalCategory["MaterialsSteelCoil"] = "environmental-category_materials_steel-coil";
    EnvironmentalCategory["MaterialsGlass"] = "environmental-category_materials_glass";
    EnvironmentalCategory["MaterialsPiping"] = "environmental-category_materials_piping";
    EnvironmentalCategory["MaterialsAluminium"] = "environmental-category_materials_aluminium";
    EnvironmentalCategory["MaterialsPlasticsSheetsFilm"] = "environmental-category_materials_plastic-sheets-film";
    EnvironmentalCategory["MaterialsComposites"] = "environmental-category_materials_composites";
    EnvironmentalCategory["MaterialsCoatingsFinishes"] = "environmental-category_materials_coatings-finishes";
    EnvironmentalCategory["MaterialsCables"] = "environmental-category_materials_cables";
    EnvironmentalCategory["MaterialsWaterTretmentChemicals"] = "environmental-category_materials_water-tretment-chemicals";
    EnvironmentalCategory["MaterialsCarpet"] = "environmental-category_materials_carpet";
    // Transport
    EnvironmentalCategory["TransportRoad"] = "environmental-category_road";
    EnvironmentalCategory["TransportRail"] = "environmental-category_rail";
    EnvironmentalCategory["TransportShip"] = "environmental-category_sea";
})(EnvironmentalCategory || (EnvironmentalCategory = {}));
export const environmentalCategoryToString = (c) => {
    switch (c) {
        case EnvironmentalCategory.EnergyElectricity:
            return "Electricity";
        case EnvironmentalCategory.EnergyFuelType:
            return "Fuel Type";
        // Water
        case EnvironmentalCategory.WaterPotable:
            return "Potable";
        case EnvironmentalCategory.WaterNonPotable:
            return "Non-Potable";
        case EnvironmentalCategory.WaterReusedOnSite:
            return "Reused On Site";
        case EnvironmentalCategory.WaterDischarge:
            return "Water Discharge";
        // Waste
        case EnvironmentalCategory.ConstructionDemolitionWaste:
            return "Construction/Demolition Waste";
        case EnvironmentalCategory.SpoilFillSoilMaterial:
            return "Spoil/Fill/Soil Material";
        case EnvironmentalCategory.SolidLiquidMixedWaste:
            return "Solid & Liquid Mixed Waste";
        case EnvironmentalCategory.OfficeWaste:
            return "Office Waste";
        // Materials
        case EnvironmentalCategory.MaterialsConcreteReadyMix:
            return "Ready Mix Concrete";
        case EnvironmentalCategory.MaterialsPrecastConcrete:
            return "Precast Concrete";
        case EnvironmentalCategory.MaterialsAsphalt:
            return "Asphalt";
        case EnvironmentalCategory.MaterialsBitumen:
            return "Bitumen";
        case EnvironmentalCategory.MaterialsWoodTimber:
            return "Wood / Timber";
        case EnvironmentalCategory.MaterialsCement:
            return "Cement";
        case EnvironmentalCategory.MaterialsAggregates:
            return "Aggregates";
        case EnvironmentalCategory.MaterialsSteel:
            return "Steel";
        case EnvironmentalCategory.MaterialsSteelCoil:
            return "Steel - Coil";
        case EnvironmentalCategory.MaterialsGlass:
            return "Glass";
        case EnvironmentalCategory.MaterialsPiping:
            return "Piping";
        case EnvironmentalCategory.MaterialsAluminium:
            return "Aluminium";
        case EnvironmentalCategory.MaterialsPlasticsSheetsFilm:
            return "Plastics - Sheets & Film";
        case EnvironmentalCategory.MaterialsComposites:
            return "Composites";
        case EnvironmentalCategory.MaterialsCoatingsFinishes:
            return "Coatings & Finishes";
        case EnvironmentalCategory.MaterialsCables:
            return "Cables";
        case EnvironmentalCategory.MaterialsWaterTretmentChemicals:
            return "Water Treatment Chemicals";
        case EnvironmentalCategory.MaterialsCarpet:
            return "Carpet";
        // Transport
        case EnvironmentalCategory.TransportRoad:
            return "Road";
        case EnvironmentalCategory.TransportRail:
            return "Rail";
        case EnvironmentalCategory.TransportShip:
            return "Sea";
        default:
            return String(c);
    }
};
// ---------------------------------------------------------------------------
// Waste Extra Fields
// ---------------------------------------------------------------------------
export var WasteParentCategory;
(function (WasteParentCategory) {
    WasteParentCategory["ConstructionDemolitionWaste"] = "waste-parent-category_construction-demolition-waste";
    WasteParentCategory["SpoilFillSoilMaterial"] = "waste-parent-category_spoil-fill-soil-material";
    WasteParentCategory["SolidLiquidMixedWaste"] = "waste-parent-category_solid-liquid-mixed-waste";
    WasteParentCategory["OfficeWaste"] = "waste-parent-category_office-waste";
})(WasteParentCategory || (WasteParentCategory = {}));
export const wasteParentCategoryToString = (c) => {
    switch (c) {
        case WasteParentCategory.ConstructionDemolitionWaste:
            return "Construction/Demolition Waste";
        case WasteParentCategory.SpoilFillSoilMaterial:
            return "Spoil/Fill/Soil Material";
        case WasteParentCategory.SolidLiquidMixedWaste:
            return "Solid & Liquid Mixed Waste";
        case WasteParentCategory.OfficeWaste:
            return "Office Waste";
    }
};
export var WasteType;
(function (WasteType) {
    WasteType["HazardousWaste"] = "waste-type_hazardous";
    WasteType["NonHazardousWaste"] = "waste-type_non-hazardous";
})(WasteType || (WasteType = {}));
export const wasteTypeToString = (w) => {
    switch (w) {
        case WasteType.HazardousWaste:
            return "Hazardous Waste";
        case WasteType.NonHazardousWaste:
            return "Non-Hazardous Waste";
    }
};
export var WasteDisposalDestination;
(function (WasteDisposalDestination) {
    WasteDisposalDestination["WaterTreatmentPlant"] = "waste-disposal-destination_water-treatment-plant";
    WasteDisposalDestination["TransferOnlyFacility"] = "waste-disposal-destination_transfer-only-facility";
    WasteDisposalDestination["ReturnedToSupplier"] = "waste-disposal-destination_returned-to-supplier";
    WasteDisposalDestination["Reuse"] = "waste-disposal-destination_reuse";
    WasteDisposalDestination["Landfill"] = "waste-disposal-destination_landfill";
    WasteDisposalDestination["Incinerated"] = "waste-disposal-destination_incinerated";
    WasteDisposalDestination["SoilFill"] = "waste-disposal-destination_soil-fill";
    WasteDisposalDestination["Recycled"] = "waste-disposal-destination_recycled";
})(WasteDisposalDestination || (WasteDisposalDestination = {}));
/**
 * Two facility-named destinations were retired here — `landfill-only-facility`
 * and `resource-recovery-facility`. They said where waste went rather than what
 * happened to it, which left disagreeing definitions of "diverted" across the
 * dashboards. Step 3 of `environmental_water_release.sql` folded every stored row
 * onto its outcome twin (landfill and recycled respectively), so no runtime
 * fallback is needed: nothing in the database still holds them.
 * `water-treatment-plant` had no outcome twin and survives as its own value.
 *
 * `other` was retired the same way by
 * `environmental_destination_simplification.sql`, folded onto
 * `transfer-only-facility`: both mean "the real outcome is unknown", both were
 * already outside `DIVERTED_WASTE_DESTINATIONS`, so no diversion figure moved.
 * These eight are now the whole list, and every one of them is offered on every
 * waste item — the project-level "choose which destinations apply" setting is
 * gone.
 */
/**
 * What counts as diverted from landfill — the single definition every surface
 * reads, so a diversion figure means the same thing on a target, the project
 * dashboard tile, the org dashboard and the waste analytics chart.
 *
 * Recycled and Reuse only. Deliberately NOT "everything except landfill":
 * incinerated waste is destroyed rather than recovered and a transfer station
 * is a waypoint whose real destination is unknown — counting either as
 * diverted inflates the figure with waste nobody has
 * shown was recovered. Soil fill and returned-to-supplier are likewise left
 * out: they are legitimate outcomes but not resource recovery, and the sector
 * frameworks these numbers are reported against do not credit them.
 */
export const DIVERTED_WASTE_DESTINATIONS = new Set([WasteDisposalDestination.Recycled, WasteDisposalDestination.Reuse]);
/** Whether a reported destination counts as diverted from landfill. */
export const isDivertedWasteDestination = (destination) => destination != null &&
    DIVERTED_WASTE_DESTINATIONS.has(destination);
/**
 * Whether a waste row's destination is one that carries a recycled share.
 *
 * Only `Recycled` does. The recycled percentage used to be typed independently
 * of the destination, so a load could be filed as Landfill and still claim 40%
 * recycled — two fields answering the same question differently, and the
 * dashboards picked whichever one they happened to read. The percentage is now
 * derived from the destination: Recycled opens the input (defaulting to 100),
 * everything else stores a truthful 0.
 *
 * Note `Reuse` is deliberately excluded even though it counts as diverted —
 * diversion from landfill and recycled content are different measures, and
 * reused material is not recycled material.
 */
export const wasteDestinationCarriesRecycledPct = (destination) => destination === WasteDisposalDestination.Recycled;
export const wasteDisposalDestinationToString = (d) => {
    switch (d) {
        case WasteDisposalDestination.WaterTreatmentPlant:
            return "Water Treatment Plant";
        case WasteDisposalDestination.TransferOnlyFacility:
            return "Transfer Only Facility";
        case WasteDisposalDestination.ReturnedToSupplier:
            return "Returned To Supplier";
        case WasteDisposalDestination.Reuse:
            return "Reuse";
        case WasteDisposalDestination.Landfill:
            return "Landfill";
        case WasteDisposalDestination.Incinerated:
            return "Incinerated";
        case WasteDisposalDestination.SoilFill:
            return "Soil/Fill";
        case WasteDisposalDestination.Recycled:
            return "Recycled";
    }
};
// ---------------------------------------------------------------------------
// Eco-labels
// ---------------------------------------------------------------------------
/**
 * Environmental certifications a MATERIAL PRODUCT can carry — the ISC-approved
 * labels, as listed in the client's own guideline document.
 *
 * Deliberately NOT `SocialSpendCertificate`. That list is social-procurement
 * bodies (Supply Nation, Social Traders, Kinaway, Australian Network on
 * Disability), it hangs off a supplier on a spend invoice, and filing GECA
 * there would both misfile environmental data as social AND count it toward
 * social spend targets.
 *
 * A property of the PRODUCT, not of the supplier: the same supplier sells
 * certified and uncertified concrete, so inheriting a label from an ABN would
 * mark every line from a partly-certified supplier as labelled — wrong in the
 * reporter's favour, which is the worst direction for a compliance figure.
 *
 * Several at once is normal (a GreenTag-certified product with its own EPD),
 * hence an array everywhere this appears.
 *
 * No `Other` and no free text, for the reason `WasteDisposalDestination` has
 * neither: an escape hatch collects rows nobody can score, and retiring one
 * later costs a data migration. Adding a label is a code change and NOT a
 * database migration — every column holding these is `text[]`.
 */
export var EcoLabel;
(function (EcoLabel) {
    EcoLabel["EcospecifierGreenTag"] = "eco-label_ecospecifier-green-tag";
    EcoLabel["GecaEcoLabel"] = "eco-label_geca-eco-label";
    EcoLabel["GbcaBestEnvironmentalPractice"] = "eco-label_gbca-best-environmental-practice";
    EcoLabel["IsealSupplyChainStewardship"] = "eco-label_iseal-supply-chain-stewardship";
    EcoLabel["EpdProductSpecific"] = "eco-label_epd-product-specific";
    EcoLabel["EpdIndustryWide"] = "eco-label_epd-industry-wide";
    /** PEFC-certified timber (Responsible Wood in Australia), chain of custody held. */
    EcoLabel["PefcChainOfCustody"] = "eco-label_pefc-chain-of-custody";
})(EcoLabel || (EcoLabel = {}));
export const ecoLabelToString = (l) => {
    switch (l) {
        case EcoLabel.EcospecifierGreenTag:
            return "Ecospecifier Green Tag";
        case EcoLabel.GecaEcoLabel:
            return "Good Environmental Choice Australia Eco-Label";
        case EcoLabel.GbcaBestEnvironmentalPractice:
            return "Green Building Council of Australia BEP";
        case EcoLabel.IsealSupplyChainStewardship:
            return "ISEAL Alliance Supply Chain Stewardship (e.g. FSC)";
        case EcoLabel.EpdProductSpecific:
            return "EPD (product specific)";
        case EcoLabel.EpdIndustryWide:
            return "EPD (industry wide)";
        case EcoLabel.PefcChainOfCustody:
            return "PEFC / Responsible Wood (chain of custody)";
    }
};
/**
 * The short form for a chip or a narrow column — the mark as the industry says
 * it, with {@link ecoLabelToString} as the tooltip behind it.
 *
 * Deliberately not derived by truncating the long name: "Good Environmental
 * Choice Australia Eco-Label" is known as GECA and "ISEAL Alliance Supply
 * Chain Stewardship" is recognised by the FSC mark on the product, so a
 * truncation would produce a label nobody uses. The two EPDs must stay
 * distinguishable at a glance, since an industry-wide EPD is a sector average
 * and a product-specific one is that product — a target can accept one and not
 * the other.
 */
export const ecoLabelToShortString = (l) => {
    switch (l) {
        case EcoLabel.EcospecifierGreenTag:
            return "GreenTag";
        case EcoLabel.GecaEcoLabel:
            return "GECA";
        case EcoLabel.GbcaBestEnvironmentalPractice:
            return "GBCA BEP";
        case EcoLabel.IsealSupplyChainStewardship:
            return "FSC / ISEAL";
        case EcoLabel.EpdProductSpecific:
            return "EPD product";
        case EcoLabel.EpdIndustryWide:
            return "EPD industry";
        case EcoLabel.PefcChainOfCustody:
            return "PEFC";
    }
};
/**
 * Whether a reported material row carries a label the target accepts.
 *
 * An empty/absent `accepted` means ANY approved label counts — the same
 * "absent = every one" wildcard `category`, `sub_category`, `emission_scopes`
 * and `waste_disposal_destinations` all use, so "a green/eco-label" needs no
 * sentinel value.
 *
 * Counts the row IN FULL when it matches, never weighted. This is the
 * `DestinationShare` rule, not the `RecycledPercentage` one: a product either
 * holds a certification or it does not, and there is no partial credit to
 * apportion.
 */
export const materialCarriesEcoLabel = (labels, accepted) => {
    if (!labels || labels.length === 0)
        return false;
    if (!accepted || accepted.length === 0)
        return true;
    return labels.some((label) => accepted.includes(label));
};
// ---------------------------------------------------------------------------
// Environmental Metric Type
// ---------------------------------------------------------------------------
export var EnvironmentalMetricType;
(function (EnvironmentalMetricType) {
    EnvironmentalMetricType["KilowattHours"] = "environmental-metric-type_kilowatt-hours";
    EnvironmentalMetricType["Litres"] = "environmental-metric-type_litres";
    EnvironmentalMetricType["Kilolitres"] = "environmental-metric-type_kilolitres";
    EnvironmentalMetricType["CubicMetres"] = "environmental-metric-type_cubic-metres";
    EnvironmentalMetricType["Kilograms"] = "environmental-metric-type_kilograms";
    EnvironmentalMetricType["Tonnes"] = "environmental-metric-type_tonnes";
    EnvironmentalMetricType["TonneKilometres"] = "environmental-metric-type_tonne-kilometres";
    EnvironmentalMetricType["Percentage"] = "environmental-metric-type_percentage";
    EnvironmentalMetricType["NumberOfCylinders"] = "environmental-metric-type_number-of-cylinders";
})(EnvironmentalMetricType || (EnvironmentalMetricType = {}));
export const environmentalMetricTypeToString = (m) => {
    switch (m) {
        case EnvironmentalMetricType.KilowattHours:
            return "Kilowatt-hours (kWh)";
        case EnvironmentalMetricType.Litres:
            return "Litres (L)";
        case EnvironmentalMetricType.Kilolitres:
            return "Kilolitres (kL)";
        case EnvironmentalMetricType.CubicMetres:
            return "Cubic Metres (m³)";
        case EnvironmentalMetricType.Kilograms:
            return "Kilograms (kg)";
        case EnvironmentalMetricType.Tonnes:
            return "Tonnes (t)";
        case EnvironmentalMetricType.TonneKilometres:
            return "Tonne / Kilometre (t/km)";
        case EnvironmentalMetricType.Percentage:
            return "Percentage (%)";
        case EnvironmentalMetricType.NumberOfCylinders:
            return "No. of Cylinders";
        default:
            return String(m);
    }
};
export const environmentalMetricTypeToUnit = (m) => {
    switch (m) {
        case EnvironmentalMetricType.KilowattHours:
            return "kWh";
        case EnvironmentalMetricType.Litres:
            return "L";
        case EnvironmentalMetricType.Kilolitres:
            return "kL";
        case EnvironmentalMetricType.CubicMetres:
            return "m³";
        case EnvironmentalMetricType.Kilograms:
            return "kg";
        case EnvironmentalMetricType.Tonnes:
            return "t";
        case EnvironmentalMetricType.TonneKilometres:
            return "t/km";
        case EnvironmentalMetricType.Percentage:
            return "%";
        case EnvironmentalMetricType.NumberOfCylinders:
            return "No. of Cylinders";
        default:
            return "";
    }
};
// ---------------------------------------------------------------------------
// Environmental SubCategory (filtered by Category)
// ---------------------------------------------------------------------------
export var EnvironmentalSubCategory;
(function (EnvironmentalSubCategory) {
    // Electricity
    EnvironmentalSubCategory["ElectricityKwh"] = "environmental-sub-category_electricity-kwh";
    // Fuel Type: diesel and biodiesel blends
    EnvironmentalSubCategory["Diesel"] = "environmental-sub-category_diesel";
    EnvironmentalSubCategory["DieselPremium"] = "environmental-sub-category_diesel-premium";
    EnvironmentalSubCategory["DieselB5Biodiesel"] = "environmental-sub-category_diesel-b5-biodiesel";
    EnvironmentalSubCategory["DieselB10Biodiesel"] = "environmental-sub-category_diesel-b10-biodiesel";
    EnvironmentalSubCategory["DieselB20Biodiesel"] = "environmental-sub-category_diesel-b20-biodiesel";
    EnvironmentalSubCategory["DieselB50Biodiesel"] = "environmental-sub-category_diesel-b50-biodiesel";
    EnvironmentalSubCategory["DieselB100Biodiesel"] = "environmental-sub-category_diesel-b100-biodiesel";
    // Fuel Type: petrol
    EnvironmentalSubCategory["Petrol"] = "environmental-sub-category_petrol";
    EnvironmentalSubCategory["PetrolE10"] = "environmental-sub-category_petrol-e10";
    // Fuel Type: gases
    EnvironmentalSubCategory["GasLpg"] = "environmental-sub-category_gas-lpg";
    EnvironmentalSubCategory["GasNatural"] = "environmental-sub-category_gas-natural";
    EnvironmentalSubCategory["GasLng"] = "environmental-sub-category_gas-lng";
    EnvironmentalSubCategory["GasCng"] = "environmental-sub-category_gas-cng";
    EnvironmentalSubCategory["GasAcetylene"] = "environmental-sub-category_acetylene";
    EnvironmentalSubCategory["GasHydrogen"] = "environmental-sub-category_hydrogen";
    // Fuel Type: oils, lubricants and solvents
    EnvironmentalSubCategory["OilHVO"] = "environmental-sub-category_oil-hvo";
    EnvironmentalSubCategory["OilLubricating"] = "environmental-sub-category_oil-lubricating";
    EnvironmentalSubCategory["OilTransmissionBrakeHydraulic"] = "environmental-sub-category_oil-transmission-brake-hydraulic";
    EnvironmentalSubCategory["OilGrease"] = "environmental-sub-category_oil-grease";
    EnvironmentalSubCategory["OilSolvents"] = "environmental-sub-category_oil-solvents";
    // Water sources
    EnvironmentalSubCategory["WaterSurfaceBrackishSeawater"] = "environmental-sub-category_water-surface-brackish-seawater";
    EnvironmentalSubCategory["WaterSurfaceFreshwater"] = "environmental-sub-category_water-surface-freshwater";
    EnvironmentalSubCategory["WaterGroundwaterRenewable"] = "environmental-sub-category_water-groundwater-renewable";
    EnvironmentalSubCategory["WaterGroundwaterNonRenewable"] = "environmental-sub-category_water-groundwater-non-renewable";
    EnvironmentalSubCategory["WaterSurfaceRenewable"] = "environmental-sub-category_water-surface-renewable";
    EnvironmentalSubCategory["WaterSurfaceNonRenewable"] = "environmental-sub-category_water-surface-non-renewable";
    EnvironmentalSubCategory["WaterThirdPartySources"] = "environmental-sub-category_water-third-party-sources";
    EnvironmentalSubCategory["WaterProducedTreated"] = "environmental-sub-category_water-produced-treated";
    EnvironmentalSubCategory["WaterRecycledOffsite"] = "environmental-sub-category_water-recycled-offsite";
    EnvironmentalSubCategory["WaterRecycledOnsite"] = "environmental-sub-category_water-recycled-onsite";
    EnvironmentalSubCategory["WaterRainwater"] = "environmental-sub-category_water-rainwater";
    EnvironmentalSubCategory["WaterOther"] = "environmental-sub-category_water-other";
    // Water discharge destinations. Deliberately separate from the source
    // members above: a row discharged to surface water is a different fact from
    // a row withdrawn from surface freshwater, and sharing a member would make
    // the two indistinguishable anywhere data is grouped by sub-category.
    EnvironmentalSubCategory["WaterDischargeSurfaceWater"] = "environmental-sub-category_water-discharge-surface-water";
    EnvironmentalSubCategory["WaterDischargeGroundwater"] = "environmental-sub-category_water-discharge-groundwater";
    EnvironmentalSubCategory["WaterDischargeTradeWasteSewer"] = "environmental-sub-category_water-discharge-trade-waste-sewer";
    // Waste - Construction/Demolition Waste
    EnvironmentalSubCategory["WastePaperCardboardCD"] = "environmental-sub-category_waste-paper-cardboard-cd";
    EnvironmentalSubCategory["WastePlasticsCD"] = "environmental-sub-category_waste-plastics-cd";
    EnvironmentalSubCategory["WasteConcreteCD"] = "environmental-sub-category_waste-concrete-cd";
    EnvironmentalSubCategory["WasteAsphaltCD"] = "environmental-sub-category_waste-asphalt-cd";
    EnvironmentalSubCategory["WasteScrapMetalCD"] = "environmental-sub-category_waste-scrap-metal-cd";
    EnvironmentalSubCategory["WasteWoodCD"] = "environmental-sub-category_waste-wood-cd";
    EnvironmentalSubCategory["WasteGreenWasteCD"] = "environmental-sub-category_waste-green-waste-cd";
    EnvironmentalSubCategory["WasteOtherCD"] = "environmental-sub-category_waste-other-cd";
    EnvironmentalSubCategory["WastePlasterboardCD"] = "environmental-sub-category_plasterboard";
    EnvironmentalSubCategory["WasteGlassCD"] = "environmental-sub-category_waste-glass-cd";
    EnvironmentalSubCategory["WasteBricksRoofTilesCD"] = "environmental-sub-category_waste-bricks-roof-tiles-cd";
    EnvironmentalSubCategory["WasteAggregatesCD"] = "environmental-sub-category_waste-aggregates-cd";
    // Waste - Spoil/Fill/Soil Material
    EnvironmentalSubCategory["WasteCleanSpoilSFS"] = "environmental-sub-category_waste-clean-spoil";
    EnvironmentalSubCategory["WasteDrillSpoilSFS"] = "environmental-sub-category_waste-drill-spoil";
    EnvironmentalSubCategory["WasteStreetSweepingsSFS"] = "environmental-sub-category_waste-street-sweepings";
    EnvironmentalSubCategory["WasteAcidSulphateSFS"] = "environmental-sub-category_waste-acid-sulphate";
    EnvironmentalSubCategory["WasteContaminatedSoilSFS"] = "environmental-sub-category_waste-contaminated-soil";
    EnvironmentalSubCategory["WasteSandSFS"] = "environmental-sub-category_waste-sand";
    // Waste - Solid & Liquid Mixed Waste
    EnvironmentalSubCategory["WasteSewageEffluentSLM"] = "environmental-sub-category_waste-sewage-effluent";
    EnvironmentalSubCategory["WasteSpillCleanupSLM"] = "environmental-sub-category_waste-spill-cleanup";
    EnvironmentalSubCategory["WasteBatteriesSLM"] = "environmental-sub-category_waste-batteries";
    EnvironmentalSubCategory["WasteConcreteSlurrySLM"] = "environmental-sub-category_waste-concrete-slurry";
    EnvironmentalSubCategory["WasteAsbestosSLM"] = "environmental-sub-category_waste-asbestos";
    EnvironmentalSubCategory["WasteOilsSLM"] = "environmental-sub-category_waste-oils";
    EnvironmentalSubCategory["WasteOilyRagsSLM"] = "environmental-sub-category_waste-oily-rags";
    EnvironmentalSubCategory["WasteOtherSLM"] = "environmental-sub-category_waste-other-slm";
    EnvironmentalSubCategory["WasteDehydratedSludgeSLM"] = "environmental-sub-category_waste-dehydrated-sludge";
    EnvironmentalSubCategory["WasteUsedTyresSLM"] = "environmental-sub-category_waste-used-tyres";
    // Waste - Office Waste
    EnvironmentalSubCategory["WasteGeneralOffice"] = "environmental-sub-category_waste-general-office";
    EnvironmentalSubCategory["WasteCoMingledRecyclingOffice"] = "environmental-sub-category_waste-co-mingled-recycling-office";
    EnvironmentalSubCategory["WasteSoftPlasticsOffice"] = "environmental-sub-category_waste-soft-plastics-office";
    EnvironmentalSubCategory["WasteOrganicsOffice"] = "environmental-sub-category_waste-organics-office";
    EnvironmentalSubCategory["WasteEWasteOffice"] = "environmental-sub-category_waste-e-waste-office";
    EnvironmentalSubCategory["WasteClinicalOffice"] = "environmental-sub-category_waste-clinical-office";
    EnvironmentalSubCategory["WasteGeneralLandfillOffice"] = "environmental-sub-category_waste-general-landfill-office";
    // Materials
    EnvironmentalSubCategory["MaterialsConcreteReadyMix"] = "environmental-sub-category_materials-concrete-ready-mix";
    EnvironmentalSubCategory["MaterialsPrecastConcrete"] = "environmental-sub-category_materials-precast-concrete";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeL"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-l";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeN"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-n";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeH"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-h";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeV"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-v";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeSI"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-si";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeSF"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-sf";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeSS"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-ss";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedTypeSG"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-type-sg";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20MediumDuty"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-ac7-ac10-ac14-ac20-medium-duty";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20HeavyDuty"] = "environmental-sub-category_materials-asphalt-cold-mix-dense-graded-ac7-ac10-ac14-ac20-heavy-duty";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixOpenGradedOGA"] = "environmental-sub-category_materials-asphalt-cold-mix-open-graded-oga";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixOpenGradedOG10"] = "environmental-sub-category_materials-asphalt-cold-mix-open-graded-og10";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixOpenGradedOG14"] = "environmental-sub-category_materials-asphalt-cold-mix-open-graded-og14";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixOpenGradedCrumbRubberOGA"] = "environmental-sub-category_materials-asphalt-cold-mix-open-graded-crumb-rubber-oga";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixStoneMasticSMA"] = "environmental-sub-category_materials-asphalt-cold-mix-stone-mastic-sma";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixStoneMasticSM10"] = "environmental-sub-category_materials-asphalt-cold-mix-stone-mastic-sm10";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixStoneMasticSM14"] = "environmental-sub-category_materials-asphalt-cold-mix-stone-mastic-sm14";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixHighModulusEME2"] = "environmental-sub-category_materials-asphalt-cold-mix-high-modulus-eme2";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixGapGradedRGG"] = "environmental-sub-category_materials-asphalt-cold-mix-gap-graded-rgg";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixCrumbRubberGapGraded"] = "environmental-sub-category_materials-asphalt-cold-mix-crumb-rubber-gap-graded";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixCrumbRubberHBCRA"] = "environmental-sub-category_materials-asphalt-cold-mix-crumb-rubber-hbcra";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixCrumbRubberLTCRA"] = "environmental-sub-category_materials-asphalt-cold-mix-crumb-rubber-ltcra";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixCrumbRubberLTCRAWetMix"] = "environmental-sub-category_materials-asphalt-cold-mix-crumb-rubber-ltcra-wet-mix";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixUltraThinSpecialisedUltraThinUTA"] = "environmental-sub-category_materials-asphalt-cold-mix-ultra-thin-specialised-ultra-thin-uta";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixUltraThinSpecialisedThinSurfacing"] = "environmental-sub-category_materials-asphalt-cold-mix-ultra-thin-specialised-thin-surfacing";
    EnvironmentalSubCategory["MaterialsAsphaltColdMixOther"] = "environmental-sub-category_materials-asphalt-cold-mix-other";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeL"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-l";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeN"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-n";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeH"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-h";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeV"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-v";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeSI"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-si";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeSF"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-sf";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeSS"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-ss";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedTypeSG"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-type-sg";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20MediumDuty"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-ac7-ac10-ac14-ac20-medium-duty";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20HeavyDuty"] = "environmental-sub-category_materials-asphalt-warm-mix-dense-graded-ac7-ac10-ac14-ac20-heavy-duty";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixOpenGradedOGA"] = "environmental-sub-category_materials-asphalt-warm-mix-open-graded-oga";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixOpenGradedOG10"] = "environmental-sub-category_materials-asphalt-warm-mix-open-graded-og10";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixOpenGradedOG14"] = "environmental-sub-category_materials-asphalt-warm-mix-open-graded-og14";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixOpenGradedCrumbRubberOGA"] = "environmental-sub-category_materials-asphalt-warm-mix-open-graded-crumb-rubber-oga";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixStoneMasticSMA"] = "environmental-sub-category_materials-asphalt-warm-mix-stone-mastic-sma";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixStoneMasticSM10"] = "environmental-sub-category_materials-asphalt-warm-mix-stone-mastic-sm10";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixStoneMasticSM14"] = "environmental-sub-category_materials-asphalt-warm-mix-stone-mastic-sm14";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixHighModulusEME2"] = "environmental-sub-category_materials-asphalt-warm-mix-high-modulus-eme2";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixGapGradedRGG"] = "environmental-sub-category_materials-asphalt-warm-mix-gap-graded-rgg";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixCrumbRubberGapGraded"] = "environmental-sub-category_materials-asphalt-warm-mix-crumb-rubber-gap-graded";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixCrumbRubberHBCRA"] = "environmental-sub-category_materials-asphalt-warm-mix-crumb-rubber-hbcra";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixCrumbRubberLTCRA"] = "environmental-sub-category_materials-asphalt-warm-mix-crumb-rubber-ltcra";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixCrumbRubberLTCRAWetMix"] = "environmental-sub-category_materials-asphalt-warm-mix-crumb-rubber-ltcra-wet-mix";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixUltraThinSpecialisedUltraThinUTA"] = "environmental-sub-category_materials-asphalt-warm-mix-ultra-thin-specialised-ultra-thin-uta";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixUltraThinSpecialisedThinSurfacing"] = "environmental-sub-category_materials-asphalt-warm-mix-ultra-thin-specialised-thin-surfacing";
    EnvironmentalSubCategory["MaterialsAsphaltWarmMixOther"] = "environmental-sub-category_materials-asphalt-warm-mix-other";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeL"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-l";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeN"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-n";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeH"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-h";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeV"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-v";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeSI"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-si";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeSF"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-sf";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeSS"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-ss";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedTypeSG"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-type-sg";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20MediumDuty"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-ac7-ac10-ac14-ac20-medium-duty";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20HeavyDuty"] = "environmental-sub-category_materials-asphalt-hot-mix-dense-graded-ac7-ac10-ac14-ac20-heavy-duty";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixOpenGradedOGA"] = "environmental-sub-category_materials-asphalt-hot-mix-open-graded-oga";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixOpenGradedOG10"] = "environmental-sub-category_materials-asphalt-hot-mix-open-graded-og10";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixOpenGradedOG14"] = "environmental-sub-category_materials-asphalt-hot-mix-open-graded-og14";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixOpenGradedCrumbRubberOGA"] = "environmental-sub-category_materials-asphalt-hot-mix-open-graded-crumb-rubber-oga";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixStoneMasticSMA"] = "environmental-sub-category_materials-asphalt-hot-mix-stone-mastic-sma";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixStoneMasticSM10"] = "environmental-sub-category_materials-asphalt-hot-mix-stone-mastic-sm10";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixStoneMasticSM14"] = "environmental-sub-category_materials-asphalt-hot-mix-stone-mastic-sm14";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixHighModulusEME2"] = "environmental-sub-category_materials-asphalt-hot-mix-high-modulus-eme2";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixGapGradedRGG"] = "environmental-sub-category_materials-asphalt-hot-mix-gap-graded-rgg";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixCrumbRubberGapGraded"] = "environmental-sub-category_materials-asphalt-hot-mix-crumb-rubber-gap-graded";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixCrumbRubberHBCRA"] = "environmental-sub-category_materials-asphalt-hot-mix-crumb-rubber-hbcra";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixCrumbRubberLTCRA"] = "environmental-sub-category_materials-asphalt-hot-mix-crumb-rubber-ltcra";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixCrumbRubberLTCRAWetMix"] = "environmental-sub-category_materials-asphalt-hot-mix-crumb-rubber-ltcra-wet-mix";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixUltraThinSpecialisedUltraThinUTA"] = "environmental-sub-category_materials-asphalt-hot-mix-ultra-thin-specialised-ultra-thin-uta";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixUltraThinSpecialisedThinSurfacing"] = "environmental-sub-category_materials-asphalt-hot-mix-ultra-thin-specialised-thin-surfacing";
    EnvironmentalSubCategory["MaterialsAsphaltHotMixOther"] = "environmental-sub-category_materials-asphalt-hot-mix-other";
    // Bitumen
    EnvironmentalSubCategory["MaterialsBitumen"] = "environmental-sub-category_bitumen";
    EnvironmentalSubCategory["MaterialsBitumenFoam3Water"] = "environmental-sub-category_bitumen-foam-3-water";
    EnvironmentalSubCategory["MaterialsBitumenPolymerModified"] = "environmental-sub-category_bitumen-polymer-modified";
    EnvironmentalSubCategory["MaterialsBitumenRubberised"] = "environmental-sub-category_bitumen-rubberised";
    // Timber
    EnvironmentalSubCategory["MaterialsTimberCLT"] = "environmental-sub-category_timber-clt";
    EnvironmentalSubCategory["MaterialsTimberSawnHardwood"] = "environmental-sub-category_timber-sawn-hardwood";
    EnvironmentalSubCategory["MaterialsTimberSawnSoftwood"] = "environmental-sub-category_timber-sawn-softwood";
    EnvironmentalSubCategory["MaterialsTimberFormPly"] = "environmental-sub-category_timber-form-ply";
    EnvironmentalSubCategory["MaterialsTimberLaminated"] = "environmental-sub-category_timber-laminated";
    EnvironmentalSubCategory["MaterialsTimberMDF"] = "environmental-sub-category_timber-mdf";
    EnvironmentalSubCategory["MaterialsTimberParticle"] = "environmental-sub-category_timber-particle";
    EnvironmentalSubCategory["MaterialsTimberMulch"] = "environmental-sub-category_timber-mulch";
    EnvironmentalSubCategory["MaterialsTimberOther"] = "environmental-sub-category_timber-other";
    // Cement
    EnvironmentalSubCategory["MaterialsCementPortland"] = "environmental-sub-category_cement-portland";
    EnvironmentalSubCategory["MaterialsCementLimeHydraulic"] = "environmental-sub-category_cement-lime-hydraulic";
    // Aggregates
    EnvironmentalSubCategory["MaterialsAggregatesCrushedRockCementTreated"] = "environmental-sub-category_aggregates-crushed-rock-cement-treated";
    EnvironmentalSubCategory["MaterialsAggregatesCrushedRockOther"] = "environmental-sub-category_aggregates-crushed-rock-other";
    EnvironmentalSubCategory["MaterialsAggregatesConcreteAggregate"] = "environmental-sub-category_aggregates-concrete-aggregate";
    EnvironmentalSubCategory["MaterialsAggregatesCrushedConcreteMasonry"] = "environmental-sub-category_aggregates-crushed-concrete-masonry";
    EnvironmentalSubCategory["MaterialsAggregatesCrushedLimestone"] = "environmental-sub-category_aggregates-crushed-limestone";
    EnvironmentalSubCategory["MaterialsAggregatesRecycledAsphaltPavementRAP"] = "environmental-sub-category_aggregates-recycled-asphalt-pavement-rap";
    EnvironmentalSubCategory["MaterialsAggregatesCrushedGlass"] = "environmental-sub-category_aggregates-crushed-glass";
    EnvironmentalSubCategory["MaterialsAggregatesSlag"] = "environmental-sub-category_aggregates-slag";
    EnvironmentalSubCategory["MaterialsAggregatesGravel"] = "environmental-sub-category_aggregates-gravel";
    EnvironmentalSubCategory["MaterialsAggregatesSand"] = "environmental-sub-category_aggregates-sand";
    EnvironmentalSubCategory["MaterialsAggregatesManufacturedSand"] = "environmental-sub-category_aggregates-manufactured-sand";
    EnvironmentalSubCategory["MaterialsAggregatesBallast"] = "environmental-sub-category_aggregates-ballast";
    EnvironmentalSubCategory["MaterialsAggregatesDrainageAggregate"] = "environmental-sub-category_aggregates-drainage-aggregate";
    EnvironmentalSubCategory["MaterialsAggregatesBeddingSand"] = "environmental-sub-category_aggregates-bedding-sand";
    EnvironmentalSubCategory["MaterialsAggregatesDustFines"] = "environmental-sub-category_aggregates-dust-fines";
    EnvironmentalSubCategory["MaterialsAggregatesTypeAFill"] = "environmental-sub-category_aggregates-type-a-fill";
    EnvironmentalSubCategory["MaterialsAggregatesTypeBFill"] = "environmental-sub-category_aggregates-type-b-fill";
    EnvironmentalSubCategory["MaterialsAggregatesTypeCFill"] = "environmental-sub-category_aggregates-type-c-fill";
    EnvironmentalSubCategory["MaterialsAggregatesOther"] = "environmental-sub-category_aggregates-other";
    // Steel
    EnvironmentalSubCategory["MaterialsSteelReinforcingBar"] = "environmental-sub-category_materials-steel-reinforcing-bar";
    EnvironmentalSubCategory["MaterialsSteelWeldedReinforcementMesh"] = "environmental-sub-category_materials-steel-welded-reinforcement-mesh";
    EnvironmentalSubCategory["MaterialsSteelBlackPipeAndTube"] = "environmental-sub-category_materials-steel-black-pipe-and-tube";
    EnvironmentalSubCategory["MaterialsSteelGalvanisedPipeAndTube"] = "environmental-sub-category_materials-steel-galvanised-pipe-and-tube";
    EnvironmentalSubCategory["MaterialsSteelHotRolled"] = "environmental-sub-category_materials-steel-hot-rolled";
    EnvironmentalSubCategory["MaterialsSteelPlate"] = "environmental-sub-category_materials-steel-plate";
    EnvironmentalSubCategory["MaterialsSteelRailLines"] = "environmental-sub-category_materials-steel-rail-lines";
    EnvironmentalSubCategory["MaterialsSteelFibres"] = "environmental-sub-category_materials-steel-fibres";
    EnvironmentalSubCategory["MaterialsSteelFibreAlternatives"] = "environmental-sub-category_materials-steel-fibre-alternatives";
    EnvironmentalSubCategory["MaterialsSteelPlasticFibres"] = "environmental-sub-category_materials-steel-plastic-fibres";
    EnvironmentalSubCategory["MaterialsSteelPlasticMesh"] = "environmental-sub-category_materials-steel-plastic-mesh";
    EnvironmentalSubCategory["MaterialsSteelHotRolledMetalCoated"] = "environmental-sub-category_materials-steel-hot-rolled-metal-coated";
    EnvironmentalSubCategory["MaterialsSteelHotRolledPowerCoated"] = "environmental-sub-category_materials-steel-hot-rolled-power-coated";
    EnvironmentalSubCategory["MaterialsSteelLowRelocationStrandAndWire"] = "environmental-sub-category_materials-steel-low-relocation-strand-and-wire";
    EnvironmentalSubCategory["MaterialsSteelStructureSteelMerchantBar"] = "environmental-sub-category_materials-steel-structure-steel-merchant-bar";
    EnvironmentalSubCategory["MaterialsSteelGlassFibreReinforcing"] = "environmental-sub-category_materials-steel-glass-fibre-reinforcing";
    EnvironmentalSubCategory["MaterialsSteelOther"] = "environmental-sub-category_materials-steel-other";
    // Steel - Coil
    EnvironmentalSubCategory["MaterialsSteelCoilPipe"] = "environmental-sub-category_materials-steel-coil-pipe";
    EnvironmentalSubCategory["MaterialsSteelCoilTube"] = "environmental-sub-category_materials-steel-coil-tube-coil";
    // Glass
    EnvironmentalSubCategory["MaterialsGlassFlat"] = "environmental-sub-category_materials-glass-flat";
    EnvironmentalSubCategory["MaterialsGlassOther"] = "environmental-sub-category_materials-glass-other";
    // Piping
    EnvironmentalSubCategory["MaterialsPipingDuctileIron"] = "environmental-sub-category_pipes-ductile-iron";
    EnvironmentalSubCategory["MaterialsPipingFibreglass"] = "environmental-sub-category_pipes-fibreglass";
    EnvironmentalSubCategory["MaterialsPipingGeopolymer"] = "environmental-sub-category_pipes-geopolymer";
    EnvironmentalSubCategory["MaterialsPipingPE"] = "environmental-sub-category_pipes-pe";
    EnvironmentalSubCategory["MaterialsPipingPVC"] = "environmental-sub-category_pipes-pvc";
    EnvironmentalSubCategory["MaterialsPipingSteel"] = "environmental-sub-category_pipes-steel";
    EnvironmentalSubCategory["MaterialsPipingHDPE"] = "environmental-sub-category_pipes-hdpe";
    EnvironmentalSubCategory["MaterialsPipingrHDPE"] = "environmental-sub-category_pipes-rhdpe";
    EnvironmentalSubCategory["MaterialsPipingMDPE"] = "environmental-sub-category_pipes-mdpe";
    EnvironmentalSubCategory["MaterialsPipingDICL"] = "environmental-sub-category_pipes-dicl";
    EnvironmentalSubCategory["MaterialsPipingRCPClass2"] = "environmental-sub-category_pipes-rcp-class-2";
    EnvironmentalSubCategory["MaterialsPipingRCPClass3"] = "environmental-sub-category_pipes-rcp-class-3";
    EnvironmentalSubCategory["MaterialsPipingRCPClass5"] = "environmental-sub-category_pipes-rcp-class-5";
    EnvironmentalSubCategory["MaterialsPipingPP"] = "environmental-sub-category_pipes-pp";
    // Aluminium
    EnvironmentalSubCategory["MaterialsAluminium"] = "environmental-sub-category_materials-aluminium";
    // Plastics - Sheets & Film
    EnvironmentalSubCategory["MaterialsPlasticsETFE"] = "environmental-sub-category_materials-plastics-etfe";
    EnvironmentalSubCategory["MaterialsPlasticsHDPE"] = "environmental-sub-category_materials-plastics-hdpe";
    EnvironmentalSubCategory["MaterialsPlasticsLDPE"] = "environmental-sub-category_materials-plastics-ldpe";
    EnvironmentalSubCategory["MaterialsPlasticsPC"] = "environmental-sub-category_materials-plastics-pc";
    EnvironmentalSubCategory["MaterialsPlasticsPTFE"] = "environmental-sub-category_materials-plastics-ptfe";
    EnvironmentalSubCategory["MaterialsPlasticsUPVC"] = "environmental-sub-category_materials-plastics-upvc";
    // Composites
    EnvironmentalSubCategory["MaterialsCompositesCementFibreboard"] = "environmental-sub-category_materials-composites-cement-fibreboard";
    EnvironmentalSubCategory["MaterialsCompositesGlassFibreReinforced"] = "environmental-sub-category_materials-composites-glass-fibre-reinforced";
    // Coatings & Finishes
    EnvironmentalSubCategory["MaterialsCoatingsFinishesImmersionZinc"] = "environmental-sub-category_materials-coatings-finishes-immersion-zinc";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesPaintSolventBased"] = "environmental-sub-category_materials-coatings-finishes-paint-solvent-based";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesPaintWaterBased"] = "environmental-sub-category_materials-coatings-finishes-paint-water-based";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesTrimVarnishesAndWoodStains"] = "environmental-sub-category_materials-coatings-finishes-trim-varnishes-and-wood-stains";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesPrimersSealersAndPrepCoats"] = "environmental-sub-category_materials-coatings-finishes-primers-sealers-and-prep-coats";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesOneOrTwoPackPerformanceCoatingsForFloorsEpoxy"] = "environmental-sub-category_materials-coatings-finishes-one-or-two-pack-performance-coatings-for-floors-epoxy";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesWaterproofingMembranesAndSealant"] = "environmental-sub-category_materials-coatings-finishes-waterproofing-membranes-and-sealant";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesAcousticSealant"] = "environmental-sub-category_materials-coatings-finishes-acoustic-sealant";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesFireRetardantSealantsAndAdhesives"] = "environmental-sub-category_materials-coatings-finishes-fire-retardant-sealants-and-adhesives";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesStructuralGlazingAdhesive"] = "environmental-sub-category_materials-coatings-finishes-structural-glazing-adhesive";
    EnvironmentalSubCategory["MaterialsCoatingsFinishesWoodFlooringAndLaminateAdhesivesAndSealants"] = "environmental-sub-category_materials-coatings-finishes-wood-flooring-and-laminate-adhesives-and-sealants";
    // Cables
    EnvironmentalSubCategory["MaterialsCablesSignalling"] = "environmental-sub-category_materials-cables-signalling";
    EnvironmentalSubCategory["MaterialsCablesPower"] = "environmental-sub-category_materials-cables-power";
    EnvironmentalSubCategory["MaterialsCablesCommunications"] = "environmental-sub-category_materials-cables-communications";
    // Water Treatment Chemicals
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsAlum"] = "environmental-sub-category_materials-water-treatment-chemicals-alum";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsCausticSoda"] = "environmental-sub-category_materials-water-treatment-chemicals-caustic-soda";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsCitricAcid"] = "environmental-sub-category_materials-water-treatment-chemicals-citric-acid";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsEthanol"] = "environmental-sub-category_materials-water-treatment-chemicals-ethanol";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsFerricChloride"] = "environmental-sub-category_materials-water-treatment-chemicals-ferric-chloride";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsFerricSulphate"] = "environmental-sub-category_materials-water-treatment-chemicals-ferric-sulphate";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsFerrousChloride"] = "environmental-sub-category_materials-water-treatment-chemicals-ferrous-chloride";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsHydratedLime"] = "environmental-sub-category_materials-water-treatment-chemicals-hydrated-lime";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsHydraulicAcid"] = "environmental-sub-category_materials-water-treatment-chemicals-hydraulic-acid";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsLiquidSugar"] = "environmental-sub-category_materials-water-treatment-chemicals-liquid-sugar";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsMethanol"] = "environmental-sub-category_materials-water-treatment-chemicals-methanol";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsMolasses"] = "environmental-sub-category_materials-water-treatment-chemicals-molasses";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsPolyAluminiumChlorohydrate"] = "environmental-sub-category_materials-water-treatment-chemicals-poly-aluminium-chlorohydrate";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsPolyelectrolyte"] = "environmental-sub-category_materials-water-treatment-chemicals-polyelectrolyte";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsPotassiumPermanganate"] = "environmental-sub-category_materials-water-treatment-chemicals-potassium-permanganate";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsSodiumBisulphite"] = "environmental-sub-category_materials-water-treatment-chemicals-sodium-bisulphite";
    EnvironmentalSubCategory["MaterialsWaterTreatmentChemicalsSodiumHypochlorite"] = "environmental-sub-category_materials-water-treatment-chemicals-sodium-hypochlorite";
    // Carpet
    EnvironmentalSubCategory["MaterialsCarpet"] = "environmental-sub-category_materials-carpet";
    EnvironmentalSubCategory["MaterialsOther"] = "environmental-sub-category_materials-other";
    // Transport
    EnvironmentalSubCategory["TransportRoadRigidTruck"] = "environmental-sub-category_transport-road-rigid-truck";
    EnvironmentalSubCategory["TransportRoadArticulatedTruck"] = "environmental-sub-category_transport-road-articulated-truck";
    EnvironmentalSubCategory["TransportRoadOther"] = "environmental-sub-category_transport-road-other";
    EnvironmentalSubCategory["TransportTrain"] = "environmental-sub-category_transport-train";
    EnvironmentalSubCategory["TransportShip"] = "environmental-sub-category_transport-ship";
    EnvironmentalSubCategory["Other"] = "environmental-sub-category_other";
})(EnvironmentalSubCategory || (EnvironmentalSubCategory = {}));
// ---------------------------------------------------------------------------
// Zod schemas for environmental commitment entities
// ---------------------------------------------------------------------------
export const projectEnvironmentalCommitmentSchema = z.object({
    category: z.enum(EnvironmentalCategory),
    created: z.date(),
    emission_factor_type: z.enum(EmissionFactorType).optional(),
    generation_source: z.custom().optional(),
    metric_type: z.enum(EnvironmentalMetricType),
    project_environmental_commitment_id: z.string(),
    project_id: z.string(),
    source_framework_id: z.string().optional(),
    source_framework_target_id: z.string().optional(),
    sub_category: z.enum(EnvironmentalSubCategory),
    sub_category_description: z.string().optional(),
    target_type: z.enum(EnvironmentalTargetType)
});
export const packageEnvironmentalCommitmentSchema = z.object({
    contractor_package_id: z.string(),
    created: z.date(),
    package_environmental_commitment_id: z.string(),
    project_environmental_commitment_id: z.string(),
    project_id: z.string()
});
export const createDefaultPackageEnvironmentalCommitment = (projectCommitment, contractorPackageId) => ({
    contractor_package_id: contractorPackageId,
    created: new Date(),
    package_environmental_commitment_id: uuid(),
    project_environmental_commitment_id: projectCommitment.project_environmental_commitment_id,
    project_id: projectCommitment.project_id
});
export const environmentalSubCategoryToString = (s) => {
    switch (s) {
        case EnvironmentalSubCategory.Other:
            return "Other";
        // Electricity
        case EnvironmentalSubCategory.ElectricityKwh:
            return "Electricity";
        // Fuel Type
        case EnvironmentalSubCategory.DieselB5Biodiesel:
            return "B5 Biodiesel";
        case EnvironmentalSubCategory.DieselB10Biodiesel:
            return "B10 Biodiesel";
        case EnvironmentalSubCategory.DieselB20Biodiesel:
            return "B20 Biodiesel";
        case EnvironmentalSubCategory.DieselB50Biodiesel:
            return "B50 Biodiesel";
        case EnvironmentalSubCategory.DieselB100Biodiesel:
            return "B100 Biodiesel";
        case EnvironmentalSubCategory.DieselPremium:
            return "Premium Diesel";
        case EnvironmentalSubCategory.Diesel:
            return "Diesel";
        case EnvironmentalSubCategory.Petrol:
            return "Petrol";
        case EnvironmentalSubCategory.PetrolE10:
            return "E10 Petrol";
        case EnvironmentalSubCategory.GasLpg:
            return "LPG";
        case EnvironmentalSubCategory.GasNatural:
            return "Natural Gas";
        case EnvironmentalSubCategory.GasLng:
            return "LNG";
        case EnvironmentalSubCategory.GasCng:
            return "CNG";
        case EnvironmentalSubCategory.GasAcetylene:
            return "Acetylene";
        case EnvironmentalSubCategory.GasHydrogen:
            return "Hydrogen";
        case EnvironmentalSubCategory.OilHVO:
            return "HVO (Hydrotreated Vegetable Oil)";
        case EnvironmentalSubCategory.OilGrease:
            return "Grease";
        case EnvironmentalSubCategory.OilSolvents:
            return "Solvents";
        case EnvironmentalSubCategory.OilTransmissionBrakeHydraulic:
            return "Oils - Transmission/Brake/Hydraulic";
        case EnvironmentalSubCategory.OilLubricating:
            return "Oils - Lubricating";
        // Water
        case EnvironmentalSubCategory.WaterSurfaceBrackishSeawater:
            return "Surface Water - Brackish/Seawater";
        case EnvironmentalSubCategory.WaterSurfaceFreshwater:
            return "Surface Water - Freshwater";
        case EnvironmentalSubCategory.WaterGroundwaterRenewable:
            return "Ground Water - Renewable";
        case EnvironmentalSubCategory.WaterGroundwaterNonRenewable:
            return "Ground Water - Non-Renewable";
        case EnvironmentalSubCategory.WaterSurfaceRenewable:
            return "Surface Water - Renewable";
        case EnvironmentalSubCategory.WaterSurfaceNonRenewable:
            return "Surface Water - Non-Renewable";
        case EnvironmentalSubCategory.WaterThirdPartySources:
            return "Third Party Sources (e.g. Mains)";
        case EnvironmentalSubCategory.WaterProducedTreated:
            return "Produced Water - Treated";
        case EnvironmentalSubCategory.WaterRecycledOffsite:
            return "Recycled Water - Offsite Fill Source";
        case EnvironmentalSubCategory.WaterRecycledOnsite:
            return "Recycled Water - Onsite Fill Source";
        case EnvironmentalSubCategory.WaterRainwater:
            return "Rainwater (reuse)";
        case EnvironmentalSubCategory.WaterDischargeSurfaceWater:
            return "Surface Water";
        case EnvironmentalSubCategory.WaterDischargeGroundwater:
            return "Groundwater";
        case EnvironmentalSubCategory.WaterDischargeTradeWasteSewer:
            return "Trade Waste / Sewer";
        case EnvironmentalSubCategory.WaterOther:
            return "Other";
        // Waste - Construction/Demolition
        case EnvironmentalSubCategory.WastePaperCardboardCD:
            return "Paper/Cardboard";
        case EnvironmentalSubCategory.WastePlasticsCD:
            return "Plastics";
        case EnvironmentalSubCategory.WasteConcreteCD:
            return "Concrete";
        case EnvironmentalSubCategory.WasteAsphaltCD:
            return "Asphalt";
        case EnvironmentalSubCategory.WasteScrapMetalCD:
            return "Scrap Metal";
        case EnvironmentalSubCategory.WasteWoodCD:
            return "Wood";
        case EnvironmentalSubCategory.WasteGreenWasteCD:
            return "Green Waste";
        case EnvironmentalSubCategory.WasteOtherCD:
            return "Other";
        case EnvironmentalSubCategory.WastePlasterboardCD:
            return "Plasterboard";
        case EnvironmentalSubCategory.WasteGlassCD:
            return "Waste Glass";
        case EnvironmentalSubCategory.WasteBricksRoofTilesCD:
            return "Bricks/Roof Tiles";
        case EnvironmentalSubCategory.WasteAggregatesCD:
            return "Aggregates";
        // Waste - Spoil/Fill/Soil Material
        case EnvironmentalSubCategory.WasteCleanSpoilSFS:
            return "Clean Spoil";
        case EnvironmentalSubCategory.WasteDrillSpoilSFS:
            return "Drill Spoil";
        case EnvironmentalSubCategory.WasteStreetSweepingsSFS:
            return "Street Sweepings";
        case EnvironmentalSubCategory.WasteAcidSulphateSFS:
            return "Acid Sulphate";
        case EnvironmentalSubCategory.WasteContaminatedSoilSFS:
            return "Contaminated Soil";
        case EnvironmentalSubCategory.WasteSandSFS:
            return "Sand";
        // Waste - Solid & Liquid Mixed
        case EnvironmentalSubCategory.WasteSewageEffluentSLM:
            return "Sewage Effluent";
        case EnvironmentalSubCategory.WasteSpillCleanupSLM:
            return "Spill Cleanup";
        case EnvironmentalSubCategory.WasteBatteriesSLM:
            return "Batteries";
        case EnvironmentalSubCategory.WasteConcreteSlurrySLM:
            return "Concrete Slurry";
        case EnvironmentalSubCategory.WasteAsbestosSLM:
            return "Asbestos";
        case EnvironmentalSubCategory.WasteOilsSLM:
            return "Oils";
        case EnvironmentalSubCategory.WasteOilyRagsSLM:
            return "Oily Rags";
        case EnvironmentalSubCategory.WasteDehydratedSludgeSLM:
            return "Dehydrated Sludge";
        case EnvironmentalSubCategory.WasteUsedTyresSLM:
            return "Used Tyres - Light or Heavy Vehicles";
        case EnvironmentalSubCategory.WasteOtherSLM:
            return "Other";
        // Waste - Office
        case EnvironmentalSubCategory.WasteGeneralOffice:
            return "General";
        case EnvironmentalSubCategory.WasteCoMingledRecyclingOffice:
            return "Co-Mingled Recycling";
        case EnvironmentalSubCategory.WasteSoftPlasticsOffice:
            return "Soft Plastics";
        case EnvironmentalSubCategory.WasteOrganicsOffice:
            return "Organics";
        case EnvironmentalSubCategory.WasteEWasteOffice:
            return "E-Waste";
        case EnvironmentalSubCategory.WasteClinicalOffice:
            return "Clinical";
        case EnvironmentalSubCategory.WasteGeneralLandfillOffice:
            return "General Landfill";
        // Materials
        case EnvironmentalSubCategory.MaterialsConcreteReadyMix:
            return "Concrete Ready Mix";
        case EnvironmentalSubCategory.MaterialsPrecastConcrete:
            return "Precast Concrete";
        // Materials - Asphalt
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeL:
            return "Cold Mix - Dense Graded Type L";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeN:
            return "Cold Mix - Dense Graded Type N";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeH:
            return "Cold Mix - Dense Graded Type H";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeV:
            return "Cold Mix - Dense Graded Type V";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSI:
            return "Cold Mix - Dense Graded Type SI";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSF:
            return "Cold Mix - Dense Graded Type SF";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSS:
            return "Cold Mix - Dense Graded Type SS";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSG:
            return "Cold Mix - Dense Graded Type SG";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20MediumDuty:
            return "Cold Mix - Dense Graded AC7/AC10/AC14/AC20 Medium Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20HeavyDuty:
            return "Cold Mix - Dense Graded AC7/AC10/AC14/AC20 Heavy Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOGA:
            return "Cold Mix - Open Graded OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG10:
            return "Cold Mix - Open Graded OG10";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG14:
            return "Cold Mix - Open Graded OG14";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedCrumbRubberOGA:
            return "Cold Mix - Open Graded Crumb Rubber OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSMA:
            return "Cold Mix - Stone Mastic SMA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM10:
            return "Cold Mix - Stone Mastic SM10";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM14:
            return "Cold Mix - Stone Mastic SM14";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixHighModulusEME2:
            return "Cold Mix - High Modulus EME2";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixGapGradedRGG:
            return "Cold Mix - Gap Graded RGG";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberGapGraded:
            return "Cold Mix - Crumb Rubber Gap Graded";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberHBCRA:
            return "Cold Mix - Crumb Rubber HBCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRA:
            return "Cold Mix - Crumb Rubber LTCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRAWetMix:
            return "Cold Mix - Crumb Rubber LTCRAWet Mix";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedUltraThinUTA:
            return "Cold Mix - Ultra Thin Specialised Ultra Thin UTA";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedThinSurfacing:
            return "Cold Mix - Ultra Thin Specialised Thin Surfacing";
        case EnvironmentalSubCategory.MaterialsAsphaltColdMixOther:
            return "Cold Mix - Other";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeL:
            return "Warm Mix - Dense Graded Type L";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeN:
            return "Warm Mix - Dense Graded Type N";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeH:
            return "Warm Mix - Dense Graded Type H";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeV:
            return "Warm Mix - Dense Graded Type V";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSI:
            return "Warm Mix - Dense Graded Type SI";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSF:
            return "Warm Mix - Dense Graded Type SF";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSS:
            return "Warm Mix - Dense Graded Type SS";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSG:
            return "Warm Mix - Dense Graded Type SG";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20MediumDuty:
            return "Warm Mix - Dense Graded AC7/AC10/AC14/AC20 Medium Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20HeavyDuty:
            return "Warm Mix - Dense Graded AC7/AC10/AC14/AC20 Heavy Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOGA:
            return "Warm Mix - Open Graded OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG10:
            return "Warm Mix - Open Graded OG10";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG14:
            return "Warm Mix - Open Graded OG14";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedCrumbRubberOGA:
            return "Warm Mix - Open Graded Crumb Rubber OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSMA:
            return "Warm Mix - Stone Mastic SMA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM10:
            return "Warm Mix - Stone Mastic SM10";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM14:
            return "Warm Mix - Stone Mastic SM14";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixHighModulusEME2:
            return "Warm Mix - High Modulus EME2";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixGapGradedRGG:
            return "Warm Mix - Gap Graded RGG";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberGapGraded:
            return "Warm Mix - Crumb Rubber Gap Graded";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberHBCRA:
            return "Warm Mix - Crumb Rubber HBCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRA:
            return "Warm Mix - Crumb Rubber LTCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRAWetMix:
            return "Warm Mix - Crumb Rubber LTCRAWet Mix";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedUltraThinUTA:
            return "Warm Mix - Ultra Thin Specialised Ultra Thin UTA";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedThinSurfacing:
            return "Warm Mix - Ultra Thin Specialised Thin Surfacing";
        case EnvironmentalSubCategory.MaterialsAsphaltWarmMixOther:
            return "Warm Mix - Other";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeL:
            return "Hot Mix - Dense Graded Type L";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeN:
            return "Hot Mix - Dense Graded Type N";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeH:
            return "Hot Mix - Dense Graded Type H";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeV:
            return "Hot Mix - Dense Graded Type V";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSI:
            return "Hot Mix - Dense Graded Type SI";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSF:
            return "Hot Mix - Dense Graded Type SF";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSS:
            return "Hot Mix - Dense Graded Type SS";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSG:
            return "Hot Mix - Dense Graded Type SG";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20MediumDuty:
            return "Hot Mix - Dense Graded AC7/AC10/AC14/AC20 Medium Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20HeavyDuty:
            return "Hot Mix - Dense Graded AC7/AC10/AC14/AC20 Heavy Duty";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOGA:
            return "Hot Mix - Open Graded OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG10:
            return "Hot Mix - Open Graded OG10";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG14:
            return "Hot Mix - Open Graded OG14";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedCrumbRubberOGA:
            return "Hot Mix - Open Graded Crumb Rubber OGA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSMA:
            return "Hot Mix - Stone Mastic SMA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM10:
            return "Hot Mix - Stone Mastic SM10";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM14:
            return "Hot Mix - Stone Mastic SM14";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixHighModulusEME2:
            return "Hot Mix - High Modulus EME2";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixGapGradedRGG:
            return "Hot Mix - Gap Graded RGG";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberGapGraded:
            return "Hot Mix - Crumb Rubber Gap Graded";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberHBCRA:
            return "Hot Mix - Crumb Rubber HBCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRA:
            return "Hot Mix - Crumb Rubber LTCRA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRAWetMix:
            return "Hot Mix - Crumb Rubber LTCRAWet Mix";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedUltraThinUTA:
            return "Hot Mix - Ultra Thin Specialised Ultra Thin UTA";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedThinSurfacing:
            return "Hot Mix - Ultra Thin Specialised Thin Surfacing";
        case EnvironmentalSubCategory.MaterialsAsphaltHotMixOther:
            return "Hot Mix - Other";
        case EnvironmentalSubCategory.MaterialsBitumen:
            return "Bitumen";
        case EnvironmentalSubCategory.MaterialsBitumenFoam3Water:
            return "Foam bitumen (3% water)";
        case EnvironmentalSubCategory.MaterialsBitumenPolymerModified:
            return "Polymer modified bitumen";
        case EnvironmentalSubCategory.MaterialsBitumenRubberised:
            return "Rubberised bitumen";
        case EnvironmentalSubCategory.MaterialsTimberCLT:
            return "CLT (cross laminated timber)";
        case EnvironmentalSubCategory.MaterialsTimberSawnHardwood:
            return "Sawn hardwood";
        case EnvironmentalSubCategory.MaterialsTimberSawnSoftwood:
            return "Sawn softwood";
        case EnvironmentalSubCategory.MaterialsTimberFormPly:
            return "Form ply";
        case EnvironmentalSubCategory.MaterialsTimberLaminated:
            return "Laminated";
        case EnvironmentalSubCategory.MaterialsTimberMDF:
            return "MDF";
        case EnvironmentalSubCategory.MaterialsTimberParticle:
            return "Particle";
        case EnvironmentalSubCategory.MaterialsTimberMulch:
            return "Mulch";
        case EnvironmentalSubCategory.MaterialsTimberOther:
            return "Other";
        // Materials - Cement
        case EnvironmentalSubCategory.MaterialsCementPortland:
            return "Portland Cement";
        case EnvironmentalSubCategory.MaterialsCementLimeHydraulic:
            return "Lime (Hydraulic)";
        // Materials - Aggregates
        case EnvironmentalSubCategory.MaterialsAggregatesCrushedRockCementTreated:
            return "Crushed rock – cement treated";
        case EnvironmentalSubCategory.MaterialsAggregatesCrushedRockOther:
            return "Crushed rock – other";
        case EnvironmentalSubCategory.MaterialsAggregatesConcreteAggregate:
            return "Concrete aggregate";
        case EnvironmentalSubCategory.MaterialsAggregatesCrushedConcreteMasonry:
            return "Crushed concrete / masonry";
        case EnvironmentalSubCategory.MaterialsAggregatesCrushedLimestone:
            return "Crushed limestone";
        case EnvironmentalSubCategory.MaterialsAggregatesRecycledAsphaltPavementRAP:
            return "Recycled asphalt pavement (RAP)";
        case EnvironmentalSubCategory.MaterialsAggregatesCrushedGlass:
            return "Crushed glass";
        case EnvironmentalSubCategory.MaterialsAggregatesSlag:
            return "Slag";
        case EnvironmentalSubCategory.MaterialsAggregatesGravel:
            return "Gravel";
        case EnvironmentalSubCategory.MaterialsAggregatesSand:
            return "Sand";
        case EnvironmentalSubCategory.MaterialsAggregatesManufacturedSand:
            return "Manufactured sand";
        case EnvironmentalSubCategory.MaterialsAggregatesBallast:
            return "Ballast";
        case EnvironmentalSubCategory.MaterialsAggregatesDrainageAggregate:
            return "Drainage aggregate";
        case EnvironmentalSubCategory.MaterialsAggregatesBeddingSand:
            return "Bedding sand";
        case EnvironmentalSubCategory.MaterialsAggregatesDustFines:
            return "Dust/fines";
        case EnvironmentalSubCategory.MaterialsAggregatesTypeAFill:
            return "Type A fill";
        case EnvironmentalSubCategory.MaterialsAggregatesTypeBFill:
            return "Type B fill";
        case EnvironmentalSubCategory.MaterialsAggregatesTypeCFill:
            return "Type C fill";
        case EnvironmentalSubCategory.MaterialsAggregatesOther:
            return "Other";
        // Materials - Steel
        case EnvironmentalSubCategory.MaterialsSteelReinforcingBar:
            return "Reinforcing Bar";
        case EnvironmentalSubCategory.MaterialsSteelWeldedReinforcementMesh:
            return "Welded Reinforcement Mesh";
        case EnvironmentalSubCategory.MaterialsSteelBlackPipeAndTube:
            return "Black Pipe & Tube";
        case EnvironmentalSubCategory.MaterialsSteelGalvanisedPipeAndTube:
            return "Galvanised Pipe & Tube";
        case EnvironmentalSubCategory.MaterialsSteelHotRolled:
            return "Hot Rolled";
        case EnvironmentalSubCategory.MaterialsSteelPlate:
            return "Plate";
        case EnvironmentalSubCategory.MaterialsSteelRailLines:
            return "Rail Lines";
        case EnvironmentalSubCategory.MaterialsSteelFibres:
            return "Fibres";
        case EnvironmentalSubCategory.MaterialsSteelFibreAlternatives:
            return "Fibre Alternatives";
        case EnvironmentalSubCategory.MaterialsSteelPlasticFibres:
            return "Plastic Fibres";
        case EnvironmentalSubCategory.MaterialsSteelPlasticMesh:
            return "Plastic Mesh";
        case EnvironmentalSubCategory.MaterialsSteelHotRolledMetalCoated:
            return "Hot Rolled Metal Coated";
        case EnvironmentalSubCategory.MaterialsSteelHotRolledPowerCoated:
            return "Hot Rolled Power Coated";
        case EnvironmentalSubCategory.MaterialsSteelLowRelocationStrandAndWire:
            return "Low Relocation Strand & Wire";
        case EnvironmentalSubCategory.MaterialsSteelStructureSteelMerchantBar:
            return "Structure Steel Merchant Bar";
        case EnvironmentalSubCategory.MaterialsSteelGlassFibreReinforcing:
            return "Glass Fibre Reinforcing";
        case EnvironmentalSubCategory.MaterialsSteelOther:
            return "Other";
        // Materials - Steel Coil
        case EnvironmentalSubCategory.MaterialsSteelCoilPipe:
            return "Pipe";
        case EnvironmentalSubCategory.MaterialsSteelCoilTube:
            return "Tube & Coil";
        // Materials - Glass
        case EnvironmentalSubCategory.MaterialsGlassFlat:
            return "Flat Glass";
        case EnvironmentalSubCategory.MaterialsGlassOther:
            return "Other";
        // Materials - Piping
        case EnvironmentalSubCategory.MaterialsPipingDuctileIron:
            return "Ductile Iron Pipes";
        case EnvironmentalSubCategory.MaterialsPipingFibreglass:
            return "Fibreglass Pipe & Tube";
        case EnvironmentalSubCategory.MaterialsPipingGeopolymer:
            return "Geopolymer Pipes";
        case EnvironmentalSubCategory.MaterialsPipingPE:
            return "PE Pipes";
        case EnvironmentalSubCategory.MaterialsPipingPVC:
            return "PVC Pipes";
        case EnvironmentalSubCategory.MaterialsPipingSteel:
            return "Steel Pipe";
        case EnvironmentalSubCategory.MaterialsPipingHDPE:
            return "HDPE Pipe";
        case EnvironmentalSubCategory.MaterialsPipingrHDPE:
            return "rHDPE Pipe (recycled)";
        case EnvironmentalSubCategory.MaterialsPipingMDPE:
            return "MDPE Pipe";
        case EnvironmentalSubCategory.MaterialsPipingDICL:
            return "DICL";
        case EnvironmentalSubCategory.MaterialsPipingRCPClass2:
            return "Reinforced Concrete Pipes (RCP) Class 2";
        case EnvironmentalSubCategory.MaterialsPipingRCPClass3:
            return "Reinforced Concrete Pipes (RCP) Class 3";
        case EnvironmentalSubCategory.MaterialsPipingRCPClass5:
            return "Reinforced Concrete Pipes (RCP) Class 5";
        case EnvironmentalSubCategory.MaterialsPipingPP:
            return "Polypropylene (PP)";
        // Materials - Aluminium
        case EnvironmentalSubCategory.MaterialsAluminium:
            return "Aluminium";
        // Materials - Plastics - Sheets & Film
        case EnvironmentalSubCategory.MaterialsPlasticsETFE:
            return "ETFE Film";
        case EnvironmentalSubCategory.MaterialsPlasticsHDPE:
            return "HDPE Film";
        case EnvironmentalSubCategory.MaterialsPlasticsLDPE:
            return "LDPE Film";
        case EnvironmentalSubCategory.MaterialsPlasticsPC:
            return "PC Sheet (e.g. Danpalon)";
        case EnvironmentalSubCategory.MaterialsPlasticsPTFE:
            return "PTFE (Teflon) Film";
        case EnvironmentalSubCategory.MaterialsPlasticsUPVC:
            return "uPVC Sheet";
        // Materials - Coatings & Finishes
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesImmersionZinc:
            return "Immersion Zinc Coating";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintSolventBased:
            return "Paint - Solvent Based";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintWaterBased:
            return "Paint - Water Based";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesTrimVarnishesAndWoodStains:
            return "Trim Varnishes & Wood Stains";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesPrimersSealersAndPrepCoats:
            return "Primers Sealers & Prep Coats";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesOneOrTwoPackPerformanceCoatingsForFloorsEpoxy:
            return "1 or 2 Pack Performance Coatings for Floors (Epoxy)";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesWaterproofingMembranesAndSealant:
            return "Waterproofing Membranes & Sealant";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesAcousticSealant:
            return "Acoustic Sealant";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesFireRetardantSealantsAndAdhesives:
            return "Fire Retardant Sealants & Adhesives";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesStructuralGlazingAdhesive:
            return "Structural Glazing Adhesive";
        case EnvironmentalSubCategory.MaterialsCoatingsFinishesWoodFlooringAndLaminateAdhesivesAndSealants:
            return "Wood Flooring & Laminate Adhesives & Sealants";
        // Materials - Cables
        case EnvironmentalSubCategory.MaterialsCablesSignalling:
            return "Signalling";
        case EnvironmentalSubCategory.MaterialsCablesPower:
            return "Power";
        case EnvironmentalSubCategory.MaterialsCablesCommunications:
            return "Communications";
        // Materials - Water Treatment Chemicals
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsAlum:
            return "Alum";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCausticSoda:
            return "Caustic Soda";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCitricAcid:
            return "Citric Acid";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsEthanol:
            return "Ethanol";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricChloride:
            return "Ferric Chloride";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricSulphate:
            return "Ferric Sulphate";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerrousChloride:
            return "Ferrous Chloride";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydratedLime:
            return "Hydrated Lime";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydraulicAcid:
            return "Hydraulic Acid";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsLiquidSugar:
            return "Liquid Sugar";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMethanol:
            return "Methanol";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMolasses:
            return "Molasses";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyAluminiumChlorohydrate:
            return "Poly Aluminium Chlorohydrate";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyelectrolyte:
            return "Polyelectrolyte";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPotassiumPermanganate:
            return "Potassium Permanganate";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumBisulphite:
            return "Sodium Bisulphite";
        case EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumHypochlorite:
            return "Sodium Hypochlorite";
        // Materials - Carpet
        case EnvironmentalSubCategory.MaterialsCarpet:
            return "Carpet";
        // Materials - Composites
        case EnvironmentalSubCategory.MaterialsCompositesCementFibreboard:
            return "Cement Fibreboard";
        case EnvironmentalSubCategory.MaterialsCompositesGlassFibreReinforced:
            return "Glass Fibre Reinforced Plastic";
        // Materials - Other
        case EnvironmentalSubCategory.MaterialsOther:
            return "Other";
        // Transport
        case EnvironmentalSubCategory.TransportRoadRigidTruck:
            return "Rigid Truck";
        case EnvironmentalSubCategory.TransportRoadArticulatedTruck:
            return "Articulated Truck";
        case EnvironmentalSubCategory.TransportRoadOther:
            return "Other";
        case EnvironmentalSubCategory.TransportTrain:
            return "Train";
        case EnvironmentalSubCategory.TransportShip:
            return "Ship";
        default:
            return String(s);
    }
};
export const isOtherEnvironmentalSubCategory = (subCategory) => subCategory === EnvironmentalSubCategory.Other ||
    /(^|-)other($|-)/i.test(String(subCategory));
export const environmentalSubCategoryDisplayString = (subCategory, subCategoryDescription) => isOtherEnvironmentalSubCategory(subCategory)
    ? subCategoryDescription?.trim() ||
        environmentalSubCategoryToString(subCategory)
    : environmentalSubCategoryToString(subCategory);
/**
 * Every category ends in an `Other` leaf, water included: a site can always
 * draw from or discharge to something the library does not name, and the leaf
 * is what makes that reportable instead of mis-filed. `Other` is a placeholder
 * until someone names it, so the tree renders one row per name already in use
 * plus the bare placeholder that adds the next.
 */
const withOtherEnvironmentalSubCategory = (subCategories) => subCategories.some(isOtherEnvironmentalSubCategory)
    ? subCategories
    : [...subCategories, EnvironmentalSubCategory.Other];
// ---------------------------------------------------------------------------
// Helper Maps
// ---------------------------------------------------------------------------
export const categoryByTargetType = {
    [EnvironmentalTargetType.EnergyAndFuelsAndLubricants]: [
        EnvironmentalCategory.EnergyElectricity,
        EnvironmentalCategory.EnergyFuelType
    ],
    [EnvironmentalTargetType.Materials]: [
        EnvironmentalCategory.MaterialsConcreteReadyMix,
        EnvironmentalCategory.MaterialsPrecastConcrete,
        EnvironmentalCategory.MaterialsAsphalt,
        EnvironmentalCategory.MaterialsBitumen,
        EnvironmentalCategory.MaterialsWoodTimber,
        EnvironmentalCategory.MaterialsCement,
        EnvironmentalCategory.MaterialsAggregates,
        EnvironmentalCategory.MaterialsSteel,
        EnvironmentalCategory.MaterialsSteelCoil,
        EnvironmentalCategory.MaterialsGlass,
        EnvironmentalCategory.MaterialsPiping,
        EnvironmentalCategory.MaterialsAluminium,
        EnvironmentalCategory.MaterialsPlasticsSheetsFilm,
        EnvironmentalCategory.MaterialsComposites,
        EnvironmentalCategory.MaterialsCoatingsFinishes,
        EnvironmentalCategory.MaterialsCables,
        EnvironmentalCategory.MaterialsWaterTretmentChemicals,
        EnvironmentalCategory.MaterialsCarpet
    ],
    [EnvironmentalTargetType.Transport]: [
        EnvironmentalCategory.TransportRoad,
        EnvironmentalCategory.TransportRail,
        EnvironmentalCategory.TransportShip
    ],
    [EnvironmentalTargetType.Waste]: [
        EnvironmentalCategory.ConstructionDemolitionWaste,
        EnvironmentalCategory.SpoilFillSoilMaterial,
        EnvironmentalCategory.SolidLiquidMixedWaste,
        EnvironmentalCategory.OfficeWaste
    ],
    [EnvironmentalTargetType.WaterUsage]: [
        EnvironmentalCategory.WaterPotable,
        EnvironmentalCategory.WaterNonPotable,
        EnvironmentalCategory.WaterReusedOnSite,
        EnvironmentalCategory.WaterDischarge
    ]
};
const baseSubCategoryByCategory = {
    [EnvironmentalCategory.ConstructionDemolitionWaste]: [
        EnvironmentalSubCategory.WastePaperCardboardCD,
        EnvironmentalSubCategory.WastePlasticsCD,
        EnvironmentalSubCategory.WasteConcreteCD,
        EnvironmentalSubCategory.WasteAsphaltCD,
        EnvironmentalSubCategory.WasteScrapMetalCD,
        EnvironmentalSubCategory.WasteWoodCD,
        EnvironmentalSubCategory.WasteGreenWasteCD,
        EnvironmentalSubCategory.WasteOtherCD,
        EnvironmentalSubCategory.WastePlasterboardCD,
        EnvironmentalSubCategory.WasteGlassCD,
        EnvironmentalSubCategory.WasteBricksRoofTilesCD,
        EnvironmentalSubCategory.WasteAggregatesCD
    ],
    // Energy
    [EnvironmentalCategory.EnergyElectricity]: [
        EnvironmentalSubCategory.ElectricityKwh
    ],
    // Every fuel, gas, oil and solvent is one leaf of one category, in the
    // client's order. Its emission factor and its energy stream both follow
    // from the leaf (`ENERGY_SUB_TO_FACTOR`, `energyStreamFor`), never from the
    // category.
    [EnvironmentalCategory.EnergyFuelType]: [
        EnvironmentalSubCategory.Diesel,
        EnvironmentalSubCategory.DieselPremium,
        EnvironmentalSubCategory.DieselB5Biodiesel,
        EnvironmentalSubCategory.DieselB10Biodiesel,
        EnvironmentalSubCategory.DieselB20Biodiesel,
        EnvironmentalSubCategory.DieselB50Biodiesel,
        EnvironmentalSubCategory.DieselB100Biodiesel,
        EnvironmentalSubCategory.Petrol,
        EnvironmentalSubCategory.PetrolE10,
        EnvironmentalSubCategory.GasLpg,
        EnvironmentalSubCategory.GasNatural,
        EnvironmentalSubCategory.GasLng,
        EnvironmentalSubCategory.GasCng,
        EnvironmentalSubCategory.GasAcetylene,
        EnvironmentalSubCategory.GasHydrogen,
        EnvironmentalSubCategory.OilLubricating,
        EnvironmentalSubCategory.OilTransmissionBrakeHydraulic,
        EnvironmentalSubCategory.OilGrease,
        EnvironmentalSubCategory.OilSolvents,
        EnvironmentalSubCategory.OilHVO
    ],
    [EnvironmentalCategory.MaterialsAggregates]: [
        EnvironmentalSubCategory.MaterialsAggregatesCrushedRockCementTreated,
        EnvironmentalSubCategory.MaterialsAggregatesCrushedRockOther,
        EnvironmentalSubCategory.MaterialsAggregatesConcreteAggregate,
        EnvironmentalSubCategory.MaterialsAggregatesCrushedConcreteMasonry,
        EnvironmentalSubCategory.MaterialsAggregatesCrushedLimestone,
        EnvironmentalSubCategory.MaterialsAggregatesRecycledAsphaltPavementRAP,
        EnvironmentalSubCategory.MaterialsAggregatesCrushedGlass,
        EnvironmentalSubCategory.MaterialsAggregatesSlag,
        EnvironmentalSubCategory.MaterialsAggregatesGravel,
        EnvironmentalSubCategory.MaterialsAggregatesSand,
        EnvironmentalSubCategory.MaterialsAggregatesManufacturedSand,
        EnvironmentalSubCategory.MaterialsAggregatesBallast,
        EnvironmentalSubCategory.MaterialsAggregatesDrainageAggregate,
        EnvironmentalSubCategory.MaterialsAggregatesBeddingSand,
        EnvironmentalSubCategory.MaterialsAggregatesDustFines,
        EnvironmentalSubCategory.MaterialsAggregatesTypeAFill,
        EnvironmentalSubCategory.MaterialsAggregatesTypeBFill,
        EnvironmentalSubCategory.MaterialsAggregatesTypeCFill,
        EnvironmentalSubCategory.MaterialsAggregatesOther
    ],
    [EnvironmentalCategory.MaterialsAluminium]: [
        EnvironmentalSubCategory.MaterialsAluminium
    ],
    [EnvironmentalCategory.MaterialsAsphalt]: [
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeL,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeN,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeH,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeV,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSI,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSF,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSS,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSG,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20MediumDuty,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20HeavyDuty,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOGA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG10,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG14,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedCrumbRubberOGA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSMA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM10,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM14,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixHighModulusEME2,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixGapGradedRGG,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberGapGraded,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberHBCRA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRAWetMix,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedUltraThinUTA,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedThinSurfacing,
        EnvironmentalSubCategory.MaterialsAsphaltColdMixOther,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeL,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeN,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeH,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeV,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSI,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSF,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSS,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSG,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20MediumDuty,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20HeavyDuty,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOGA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG10,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG14,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedCrumbRubberOGA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSMA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM10,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM14,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixHighModulusEME2,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixGapGradedRGG,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberGapGraded,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberHBCRA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRAWetMix,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedUltraThinUTA,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedThinSurfacing,
        EnvironmentalSubCategory.MaterialsAsphaltWarmMixOther,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeL,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeN,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeH,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeV,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSI,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSF,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSS,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSG,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20MediumDuty,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20HeavyDuty,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOGA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG10,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG14,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedCrumbRubberOGA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSMA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM10,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM14,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixHighModulusEME2,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixGapGradedRGG,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberGapGraded,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberHBCRA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRAWetMix,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedUltraThinUTA,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedThinSurfacing,
        EnvironmentalSubCategory.MaterialsAsphaltHotMixOther
    ],
    [EnvironmentalCategory.MaterialsBitumen]: [
        EnvironmentalSubCategory.MaterialsBitumen,
        EnvironmentalSubCategory.MaterialsBitumenFoam3Water,
        EnvironmentalSubCategory.MaterialsBitumenPolymerModified,
        EnvironmentalSubCategory.MaterialsBitumenRubberised
    ],
    [EnvironmentalCategory.MaterialsCables]: [
        EnvironmentalSubCategory.MaterialsCablesSignalling,
        EnvironmentalSubCategory.MaterialsCablesPower,
        EnvironmentalSubCategory.MaterialsCablesCommunications
    ],
    [EnvironmentalCategory.MaterialsCarpet]: [
        EnvironmentalSubCategory.MaterialsCarpet
    ],
    [EnvironmentalCategory.MaterialsCement]: [
        EnvironmentalSubCategory.MaterialsCementPortland,
        EnvironmentalSubCategory.MaterialsCementLimeHydraulic
    ],
    [EnvironmentalCategory.MaterialsCoatingsFinishes]: [
        EnvironmentalSubCategory.MaterialsCoatingsFinishesImmersionZinc,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintSolventBased,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintWaterBased,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesTrimVarnishesAndWoodStains,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesPrimersSealersAndPrepCoats,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesOneOrTwoPackPerformanceCoatingsForFloorsEpoxy,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesWaterproofingMembranesAndSealant,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesAcousticSealant,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesFireRetardantSealantsAndAdhesives,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesStructuralGlazingAdhesive,
        EnvironmentalSubCategory.MaterialsCoatingsFinishesWoodFlooringAndLaminateAdhesivesAndSealants
    ],
    [EnvironmentalCategory.MaterialsComposites]: [
        EnvironmentalSubCategory.MaterialsCompositesCementFibreboard,
        EnvironmentalSubCategory.MaterialsCompositesGlassFibreReinforced
    ],
    // Materials
    [EnvironmentalCategory.MaterialsConcreteReadyMix]: [
        EnvironmentalSubCategory.MaterialsConcreteReadyMix
    ],
    [EnvironmentalCategory.MaterialsGlass]: [
        EnvironmentalSubCategory.MaterialsGlassFlat,
        EnvironmentalSubCategory.MaterialsGlassOther
    ],
    [EnvironmentalCategory.MaterialsPiping]: [
        EnvironmentalSubCategory.MaterialsPipingDuctileIron,
        EnvironmentalSubCategory.MaterialsPipingFibreglass,
        EnvironmentalSubCategory.MaterialsPipingGeopolymer,
        EnvironmentalSubCategory.MaterialsPipingPE,
        EnvironmentalSubCategory.MaterialsPipingPVC,
        EnvironmentalSubCategory.MaterialsPipingSteel,
        EnvironmentalSubCategory.MaterialsPipingHDPE,
        EnvironmentalSubCategory.MaterialsPipingrHDPE,
        EnvironmentalSubCategory.MaterialsPipingMDPE,
        EnvironmentalSubCategory.MaterialsPipingDICL,
        EnvironmentalSubCategory.MaterialsPipingRCPClass2,
        EnvironmentalSubCategory.MaterialsPipingRCPClass3,
        EnvironmentalSubCategory.MaterialsPipingRCPClass5,
        EnvironmentalSubCategory.MaterialsPipingPP
    ],
    [EnvironmentalCategory.MaterialsPlasticsSheetsFilm]: [
        EnvironmentalSubCategory.MaterialsPlasticsETFE,
        EnvironmentalSubCategory.MaterialsPlasticsHDPE,
        EnvironmentalSubCategory.MaterialsPlasticsLDPE,
        EnvironmentalSubCategory.MaterialsPlasticsPC,
        EnvironmentalSubCategory.MaterialsPlasticsPTFE,
        EnvironmentalSubCategory.MaterialsPlasticsUPVC
    ],
    [EnvironmentalCategory.MaterialsPrecastConcrete]: [
        EnvironmentalSubCategory.MaterialsPrecastConcrete
    ],
    [EnvironmentalCategory.MaterialsSteel]: [
        EnvironmentalSubCategory.MaterialsSteelReinforcingBar,
        EnvironmentalSubCategory.MaterialsSteelWeldedReinforcementMesh,
        EnvironmentalSubCategory.MaterialsSteelBlackPipeAndTube,
        EnvironmentalSubCategory.MaterialsSteelGalvanisedPipeAndTube,
        EnvironmentalSubCategory.MaterialsSteelHotRolled,
        EnvironmentalSubCategory.MaterialsSteelPlate,
        EnvironmentalSubCategory.MaterialsSteelRailLines,
        EnvironmentalSubCategory.MaterialsSteelFibres,
        EnvironmentalSubCategory.MaterialsSteelFibreAlternatives,
        EnvironmentalSubCategory.MaterialsSteelPlasticFibres,
        EnvironmentalSubCategory.MaterialsSteelPlasticMesh,
        EnvironmentalSubCategory.MaterialsSteelHotRolledMetalCoated,
        EnvironmentalSubCategory.MaterialsSteelHotRolledPowerCoated,
        EnvironmentalSubCategory.MaterialsSteelLowRelocationStrandAndWire,
        EnvironmentalSubCategory.MaterialsSteelStructureSteelMerchantBar,
        EnvironmentalSubCategory.MaterialsSteelGlassFibreReinforcing,
        EnvironmentalSubCategory.MaterialsSteelOther
    ],
    [EnvironmentalCategory.MaterialsSteelCoil]: [
        EnvironmentalSubCategory.MaterialsSteelCoilPipe,
        EnvironmentalSubCategory.MaterialsSteelCoilTube
    ],
    [EnvironmentalCategory.MaterialsWaterTretmentChemicals]: [
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsAlum,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCausticSoda,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCitricAcid,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsEthanol,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricChloride,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricSulphate,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerrousChloride,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydratedLime,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydraulicAcid,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsLiquidSugar,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMethanol,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMolasses,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyAluminiumChlorohydrate,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyelectrolyte,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPotassiumPermanganate,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumBisulphite,
        EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumHypochlorite
    ],
    [EnvironmentalCategory.MaterialsWoodTimber]: [
        EnvironmentalSubCategory.MaterialsTimberCLT,
        EnvironmentalSubCategory.MaterialsTimberSawnHardwood,
        EnvironmentalSubCategory.MaterialsTimberSawnSoftwood,
        EnvironmentalSubCategory.MaterialsTimberFormPly,
        EnvironmentalSubCategory.MaterialsTimberLaminated,
        EnvironmentalSubCategory.MaterialsTimberMDF,
        EnvironmentalSubCategory.MaterialsTimberParticle,
        EnvironmentalSubCategory.MaterialsTimberMulch,
        EnvironmentalSubCategory.MaterialsTimberOther
    ],
    // Waste
    [EnvironmentalCategory.OfficeWaste]: [
        EnvironmentalSubCategory.WasteGeneralOffice,
        EnvironmentalSubCategory.WasteCoMingledRecyclingOffice,
        EnvironmentalSubCategory.WasteSoftPlasticsOffice,
        EnvironmentalSubCategory.WasteClinicalOffice,
        EnvironmentalSubCategory.WasteOrganicsOffice,
        EnvironmentalSubCategory.WasteEWasteOffice,
        EnvironmentalSubCategory.WasteGeneralLandfillOffice
    ],
    [EnvironmentalCategory.SolidLiquidMixedWaste]: [
        EnvironmentalSubCategory.WasteSewageEffluentSLM,
        EnvironmentalSubCategory.WasteSpillCleanupSLM,
        EnvironmentalSubCategory.WasteBatteriesSLM,
        EnvironmentalSubCategory.WasteConcreteSlurrySLM,
        EnvironmentalSubCategory.WasteAsbestosSLM,
        EnvironmentalSubCategory.WasteOilsSLM,
        EnvironmentalSubCategory.WasteOtherSLM,
        EnvironmentalSubCategory.WasteDehydratedSludgeSLM,
        EnvironmentalSubCategory.WasteUsedTyresSLM
    ],
    [EnvironmentalCategory.SpoilFillSoilMaterial]: [
        EnvironmentalSubCategory.WasteCleanSpoilSFS,
        EnvironmentalSubCategory.WasteDrillSpoilSFS,
        EnvironmentalSubCategory.WasteStreetSweepingsSFS,
        EnvironmentalSubCategory.WasteAcidSulphateSFS,
        EnvironmentalSubCategory.WasteContaminatedSoilSFS,
        EnvironmentalSubCategory.WasteSandSFS
    ],
    // Transport
    [EnvironmentalCategory.TransportRail]: [
        EnvironmentalSubCategory.TransportTrain
    ],
    [EnvironmentalCategory.TransportRoad]: [
        EnvironmentalSubCategory.TransportRoadRigidTruck,
        EnvironmentalSubCategory.TransportRoadArticulatedTruck,
        EnvironmentalSubCategory.TransportRoadOther
    ],
    [EnvironmentalCategory.TransportShip]: [
        EnvironmentalSubCategory.TransportShip
    ],
    // Water. Each category now offers only the sources that can actually be
    // filed under it, rather than the full source library four times over. The
    // three source categories partition the sources between them, so a given
    // source appears under exactly one category. None of them lists an `Other`:
    // `withOtherEnvironmentalSubCategory` appends the shared, nameable one to
    // every category in the library, and water is not an exception to that.
    [EnvironmentalCategory.WaterDischarge]: [
        EnvironmentalSubCategory.WaterDischargeSurfaceWater,
        EnvironmentalSubCategory.WaterDischargeGroundwater,
        EnvironmentalSubCategory.WaterDischargeTradeWasteSewer
    ],
    [EnvironmentalCategory.WaterNonPotable]: [
        EnvironmentalSubCategory.WaterSurfaceFreshwater,
        EnvironmentalSubCategory.WaterSurfaceBrackishSeawater,
        EnvironmentalSubCategory.WaterSurfaceRenewable,
        EnvironmentalSubCategory.WaterSurfaceNonRenewable,
        EnvironmentalSubCategory.WaterGroundwaterRenewable,
        EnvironmentalSubCategory.WaterGroundwaterNonRenewable,
        EnvironmentalSubCategory.WaterRecycledOffsite
    ],
    [EnvironmentalCategory.WaterPotable]: [
        EnvironmentalSubCategory.WaterThirdPartySources,
        EnvironmentalSubCategory.WaterProducedTreated
    ],
    [EnvironmentalCategory.WaterReusedOnSite]: [
        EnvironmentalSubCategory.WaterRecycledOnsite,
        EnvironmentalSubCategory.WaterRainwater
    ]
};
/**
 * Water sources that count as recycled or reused rather than drawn fresh.
 *
 * Rainwater is included: its own label is "Rainwater (reuse)", and every water
 * category offers it beside the two recycled fills. Excluding it would make a
 * "recycled / reused" figure that ignores half of what the industry counts as
 * reuse.
 */
export const RECYCLED_WATER_SUB_CATEGORIES = [
    EnvironmentalSubCategory.WaterRecycledOffsite,
    EnvironmentalSubCategory.WaterRecycledOnsite,
    EnvironmentalSubCategory.WaterRainwater
];
export const subCategoryByCategory = Object.fromEntries(Object.entries(baseSubCategoryByCategory).map(([category, subCategories]) => [
    category,
    withOtherEnvironmentalSubCategory(subCategories)
]));
/**
 * Whether the library still holds this leaf.
 *
 * A commitment row outlives the enum it was written with: rename a
 * sub-category or restructure a family of categories, and every row already
 * pointing at the old value becomes an orphan. Nothing deletes it, and nothing
 * migrates it.
 *
 * The drawer walks the LIBRARY, so an orphan renders nowhere — but anything
 * that walks the COMMITMENTS still sees it, which is how a project ended up
 * with "1 of 2 emission factors entered" naming a factor that had no field
 * anywhere to fill it. Callers that answer questions about what a project
 * reports on must ask this first.
 */
export const isKnownEnvironmentalLeaf = (leaf) => (subCategoryByCategory[leaf.category] ?? []).includes(leaf.sub_category);
export const metricBySubCategory = {
    // Fuel Type: diesel and biodiesel blends
    [EnvironmentalSubCategory.Diesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselB5Biodiesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselB10Biodiesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselB20Biodiesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselB50Biodiesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselB100Biodiesel]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.DieselPremium]: EnvironmentalMetricType.Litres,
    // Electricity
    [EnvironmentalSubCategory.ElectricityKwh]: EnvironmentalMetricType.KilowattHours,
    // Fuel Type: gases
    [EnvironmentalSubCategory.GasAcetylene]: EnvironmentalMetricType.NumberOfCylinders,
    [EnvironmentalSubCategory.GasCng]: EnvironmentalMetricType.CubicMetres,
    [EnvironmentalSubCategory.GasHydrogen]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.GasLng]: EnvironmentalMetricType.CubicMetres,
    [EnvironmentalSubCategory.GasLpg]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.GasNatural]: EnvironmentalMetricType.CubicMetres,
    // Materials - Aggregates
    [EnvironmentalSubCategory.MaterialsAggregatesBallast]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesBeddingSand]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesConcreteAggregate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesCrushedConcreteMasonry]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesCrushedGlass]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesCrushedLimestone]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesCrushedRockCementTreated]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesCrushedRockOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesDrainageAggregate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesDustFines]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesGravel]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesManufacturedSand]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesRecycledAsphaltPavementRAP]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesSand]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesSlag]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesTypeAFill]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesTypeBFill]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAggregatesTypeCFill]: EnvironmentalMetricType.Tonnes,
    // Materials - Aluminium
    [EnvironmentalSubCategory.MaterialsAluminium]: EnvironmentalMetricType.Tonnes,
    // Materials - Asphalt (cold mix)
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberGapGraded]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberHBCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixCrumbRubberLTCRAWetMix]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20HeavyDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedAC7AC10AC14AC20MediumDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeH]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeL]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeN]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSF]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSI]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeSS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixDenseGradedTypeV]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixGapGradedRGG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixHighModulusEME2]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedCrumbRubberOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOG14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixOpenGradedOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSM14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixStoneMasticSMA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedThinSurfacing]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltColdMixUltraThinSpecialisedUltraThinUTA]: EnvironmentalMetricType.Tonnes,
    // Materials - Asphalt (hot mix)
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberGapGraded]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberHBCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixCrumbRubberLTCRAWetMix]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20HeavyDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedAC7AC10AC14AC20MediumDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeH]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeL]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeN]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSF]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSI]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeSS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixDenseGradedTypeV]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixGapGradedRGG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixHighModulusEME2]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedCrumbRubberOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOG14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixOpenGradedOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSM14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixStoneMasticSMA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedThinSurfacing]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltHotMixUltraThinSpecialisedUltraThinUTA]: EnvironmentalMetricType.Tonnes,
    // Materials - Asphalt (warm mix)
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberGapGraded]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberHBCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixCrumbRubberLTCRAWetMix]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20HeavyDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedAC7AC10AC14AC20MediumDuty]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeH]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeL]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeN]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSF]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSI]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeSS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixDenseGradedTypeV]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixGapGradedRGG]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixHighModulusEME2]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedCrumbRubberOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOG14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixOpenGradedOGA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM10]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSM14]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixStoneMasticSMA]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedThinSurfacing]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsAsphaltWarmMixUltraThinSpecialisedUltraThinUTA]: EnvironmentalMetricType.Tonnes,
    // Materials - Bitumen
    [EnvironmentalSubCategory.MaterialsBitumen]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsBitumenFoam3Water]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsBitumenPolymerModified]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsBitumenRubberised]: EnvironmentalMetricType.Tonnes,
    // Materials - Cables
    [EnvironmentalSubCategory.MaterialsCablesCommunications]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCablesPower]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCablesSignalling]: EnvironmentalMetricType.Tonnes,
    // Carpet
    [EnvironmentalSubCategory.MaterialsCarpet]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCementLimeHydraulic]: EnvironmentalMetricType.Tonnes,
    // Materials - Cement
    [EnvironmentalSubCategory.MaterialsCementPortland]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesAcousticSealant]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesFireRetardantSealantsAndAdhesives]: EnvironmentalMetricType.Tonnes,
    // Coatings & Finishes
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesImmersionZinc]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesOneOrTwoPackPerformanceCoatingsForFloorsEpoxy]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintSolventBased]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesPaintWaterBased]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesPrimersSealersAndPrepCoats]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesStructuralGlazingAdhesive]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesTrimVarnishesAndWoodStains]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesWaterproofingMembranesAndSealant]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCoatingsFinishesWoodFlooringAndLaminateAdhesivesAndSealants]: EnvironmentalMetricType.Tonnes,
    // Composites
    [EnvironmentalSubCategory.MaterialsCompositesCementFibreboard]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsCompositesGlassFibreReinforced]: EnvironmentalMetricType.Tonnes,
    // Materials
    [EnvironmentalSubCategory.MaterialsConcreteReadyMix]: EnvironmentalMetricType.CubicMetres,
    // Glass
    [EnvironmentalSubCategory.MaterialsGlassFlat]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsGlassOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingDICL]: EnvironmentalMetricType.Tonnes,
    // Piping
    [EnvironmentalSubCategory.MaterialsPipingDuctileIron]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingFibreglass]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingGeopolymer]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingHDPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingMDPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingPP]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingPVC]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingRCPClass2]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingRCPClass3]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingRCPClass5]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingrHDPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPipingSteel]: EnvironmentalMetricType.Tonnes,
    // Plastics - Sheets & Film
    [EnvironmentalSubCategory.MaterialsPlasticsETFE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPlasticsHDPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPlasticsLDPE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPlasticsPC]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPlasticsPTFE]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPlasticsUPVC]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsPrecastConcrete]: EnvironmentalMetricType.CubicMetres,
    [EnvironmentalSubCategory.MaterialsSteelBlackPipeAndTube]: EnvironmentalMetricType.Tonnes,
    // Steel - Coil
    [EnvironmentalSubCategory.MaterialsSteelCoilPipe]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelCoilTube]: EnvironmentalMetricType.Tonnes,
    // Materials - Steel
    [EnvironmentalSubCategory.MaterialsSteelFibreAlternatives]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelFibres]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelGalvanisedPipeAndTube]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelGlassFibreReinforcing]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelHotRolled]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelHotRolledMetalCoated]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelHotRolledPowerCoated]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelLowRelocationStrandAndWire]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelPlasticFibres]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelPlasticMesh]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelPlate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelRailLines]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelReinforcingBar]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelStructureSteelMerchantBar]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsSteelWeldedReinforcementMesh]: EnvironmentalMetricType.Tonnes,
    // Materials - Timber
    [EnvironmentalSubCategory.MaterialsTimberCLT]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberFormPly]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberLaminated]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberMDF]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberMulch]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberOther]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberParticle]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberSawnHardwood]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsTimberSawnSoftwood]: EnvironmentalMetricType.Tonnes,
    // Water Treatment Chemicals
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsAlum]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCausticSoda]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsCitricAcid]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsEthanol]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricChloride]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerricSulphate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsFerrousChloride]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydratedLime]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsHydraulicAcid]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsLiquidSugar]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMethanol]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsMolasses]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyAluminiumChlorohydrate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPolyelectrolyte]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsPotassiumPermanganate]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumBisulphite]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.MaterialsWaterTreatmentChemicalsSodiumHypochlorite]: EnvironmentalMetricType.Tonnes,
    // Fuel Type: oils, lubricants and solvents. Grease and solvents are in
    // litres because their NGA factors are per litre.
    [EnvironmentalSubCategory.OilGrease]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.OilHVO]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.OilLubricating]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.OilSolvents]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.OilTransmissionBrakeHydraulic]: EnvironmentalMetricType.Litres,
    // Fuel Type: petrol
    [EnvironmentalSubCategory.Petrol]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.PetrolE10]: EnvironmentalMetricType.Litres,
    // Transport
    [EnvironmentalSubCategory.TransportRoadArticulatedTruck]: EnvironmentalMetricType.TonneKilometres,
    [EnvironmentalSubCategory.TransportRoadOther]: EnvironmentalMetricType.TonneKilometres,
    [EnvironmentalSubCategory.TransportRoadRigidTruck]: EnvironmentalMetricType.TonneKilometres,
    [EnvironmentalSubCategory.TransportShip]: EnvironmentalMetricType.TonneKilometres,
    [EnvironmentalSubCategory.TransportTrain]: EnvironmentalMetricType.TonneKilometres,
    [EnvironmentalSubCategory.WasteAcidSulphateSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteAggregatesCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteAsbestosSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteAsphaltCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteBatteriesSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteBricksRoofTilesCD]: EnvironmentalMetricType.Tonnes,
    // Waste - Spoil/Fill/Soil Material
    [EnvironmentalSubCategory.WasteCleanSpoilSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteClinicalOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteCoMingledRecyclingOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteConcreteCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteConcreteSlurrySLM]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.WasteContaminatedSoilSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteDehydratedSludgeSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteDrillSpoilSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteEWasteOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteGeneralLandfillOffice]: EnvironmentalMetricType.Tonnes,
    // Waste - Office Waste
    [EnvironmentalSubCategory.WasteGeneralOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteGlassCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteGreenWasteCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteOilsSLM]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.WasteOilyRagsSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteOrganicsOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteOtherCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteOtherSLM]: EnvironmentalMetricType.Tonnes,
    // Waste - Construction/Demolition Waste
    [EnvironmentalSubCategory.WastePaperCardboardCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WastePlasterboardCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WastePlasticsCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteSandSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteScrapMetalCD]: EnvironmentalMetricType.Tonnes,
    // Waste - Solid & Liquid Mixed Waste
    [EnvironmentalSubCategory.WasteSewageEffluentSLM]: EnvironmentalMetricType.Litres,
    [EnvironmentalSubCategory.WasteSoftPlasticsOffice]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteSpillCleanupSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteStreetSweepingsSFS]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteUsedTyresSLM]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WasteWoodCD]: EnvironmentalMetricType.Tonnes,
    [EnvironmentalSubCategory.WaterDischargeGroundwater]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterDischargeSurfaceWater]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterDischargeTradeWasteSewer]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterGroundwaterNonRenewable]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterGroundwaterRenewable]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterOther]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterProducedTreated]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterRainwater]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterRecycledOffsite]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterRecycledOnsite]: EnvironmentalMetricType.Kilolitres,
    // Potable Water
    [EnvironmentalSubCategory.WaterSurfaceBrackishSeawater]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterSurfaceFreshwater]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterSurfaceNonRenewable]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterSurfaceRenewable]: EnvironmentalMetricType.Kilolitres,
    [EnvironmentalSubCategory.WaterThirdPartySources]: EnvironmentalMetricType.Kilolitres
};
const getDefaultMetricForCategory = (category) => {
    const firstNonOtherSubCategory = (baseSubCategoryByCategory[category] || []).find((subCategory) => !isOtherEnvironmentalSubCategory(subCategory));
    return ((firstNonOtherSubCategory
        ? metricBySubCategory[firstNonOtherSubCategory]
        : undefined) ?? EnvironmentalMetricType.Tonnes);
};
const getMetricTypeForEnvironmentalSelection = (args) => metricBySubCategory[args.subCategory] ??
    getDefaultMetricForCategory(args.category);
// ---------------------------------------------------------------------------
// Extra Fields (Monthly Reporting Only)
// ---------------------------------------------------------------------------
export var EnvironmentalUsedFor;
(function (EnvironmentalUsedFor) {
    EnvironmentalUsedFor["EnergyPlantAndEquipment"] = "energy-used-for_plant-equipment";
    EnvironmentalUsedFor["EnergyHeavyVehicles"] = "energy-used-for_heavy-vehicles";
    EnvironmentalUsedFor["EnergyLightVehicles"] = "energy-used-for_light-vehicles";
    EnvironmentalUsedFor["EnergyStationaryUseUnder500kWh"] = "energy-used-for_stationary-use-under-500kwh";
    EnvironmentalUsedFor["EnergyStationaryUseOver500kWh"] = "energy-used-for_stationary-use-over-500kwh";
    EnvironmentalUsedFor["EnergyNotApplicable"] = "energy-used-for_not-applicable";
    EnvironmentalUsedFor["WaterDustSuppressionCompaction"] = "water-used-for_dust-suppression";
    EnvironmentalUsedFor["WaterOfficesSheds"] = "water-used-for_offices-sheds";
    EnvironmentalUsedFor["WaterWashdown"] = "water-used-for_washdown";
    EnvironmentalUsedFor["WaterOther"] = "water-used-for_other";
})(EnvironmentalUsedFor || (EnvironmentalUsedFor = {}));
export const usedForByTargetType = {
    [EnvironmentalTargetType.EnergyAndFuelsAndLubricants]: [
        EnvironmentalUsedFor.EnergyPlantAndEquipment,
        EnvironmentalUsedFor.EnergyHeavyVehicles,
        EnvironmentalUsedFor.EnergyLightVehicles,
        EnvironmentalUsedFor.EnergyStationaryUseUnder500kWh,
        EnvironmentalUsedFor.EnergyStationaryUseOver500kWh,
        EnvironmentalUsedFor.EnergyNotApplicable
    ],
    [EnvironmentalTargetType.WaterUsage]: [
        EnvironmentalUsedFor.WaterDustSuppressionCompaction,
        EnvironmentalUsedFor.WaterOfficesSheds,
        EnvironmentalUsedFor.WaterWashdown,
        EnvironmentalUsedFor.WaterOther
    ]
};
export const getUsedForForEnvironmentalCommitment = (targetType) => {
    return usedForByTargetType[targetType] || [];
};
export const usedForToString = (w) => {
    switch (w) {
        case EnvironmentalUsedFor.EnergyPlantAndEquipment:
            return "Plant & Equipment";
        case EnvironmentalUsedFor.EnergyHeavyVehicles:
            return "Heavy Vehicles";
        case EnvironmentalUsedFor.EnergyLightVehicles:
            return "Light Vehicles";
        case EnvironmentalUsedFor.EnergyStationaryUseUnder500kWh:
            return "Stationary Use - Generators < 500kWh";
        case EnvironmentalUsedFor.EnergyStationaryUseOver500kWh:
            return "Stationary Use - Generators > 500kWh";
        case EnvironmentalUsedFor.EnergyNotApplicable:
            return "Not Applicable";
        case EnvironmentalUsedFor.WaterDustSuppressionCompaction:
            return "Dust Suppression & Compaction";
        case EnvironmentalUsedFor.WaterOfficesSheds:
            return "Offices & Sheds";
        case EnvironmentalUsedFor.WaterWashdown:
            return "Washdown";
        case EnvironmentalUsedFor.WaterOther:
            return "Other";
    }
};
/**
 * Discharge is the one water category that is not consumption, so it has no
 * use to record.
 */
export const waterCategoryAsksUsedFor = (category) => category !== EnvironmentalCategory.WaterDischarge;
/** A use the library does not name must be described by the reporter. */
export const usedForRequiresDescription = (usedFor) => usedFor === EnvironmentalUsedFor.WaterOther;
/** The reporter's description for `Other`, otherwise the library label. */
export const usedForDisplayString = (usedFor, usedForDescription) => usedForRequiresDescription(usedFor)
    ? usedForDescription?.trim() || usedForToString(usedFor)
    : usedForToString(usedFor);
/**
 * Whether a leaf is a Fuel Type `Other` with no name.
 *
 * Every fuel, gas, oil and solvent shares the one Fuel Type category, so an
 * unnamed `Other` there says nothing at all about what was burned — not even
 * the family it came from, which the four old categories used to. It also has
 * no emission factor, so a name is the only thing that makes the figure
 * reviewable. The setup UI only writes named Others; this is the rule the
 * server holds the rows to.
 */
export const isUnnamedFuelTypeOther = (c) => c.category === EnvironmentalCategory.EnergyFuelType &&
    isOtherEnvironmentalSubCategory(c.sub_category) &&
    !c.sub_category_description?.trim();
/** Returns an error message when the combination is invalid; otherwise null. */
export const validateProjectEnvironmentalCommitmentShape = (c) => {
    const categoriesForTarget = categoryByTargetType[c.target_type];
    if (!categoriesForTarget?.includes(c.category)) {
        return `Invalid environmental commitment: category "${c.category}" is not valid for target type "${c.target_type}"`;
    }
    const subCategoriesForCategory = subCategoryByCategory[c.category];
    if (!subCategoriesForCategory?.includes(c.sub_category)) {
        return `Invalid environmental commitment: sub_category "${c.sub_category}" is not valid for category "${c.category}"`;
    }
    const expectedMetric = getMetricTypeForEnvironmentalSelection({
        category: c.category,
        subCategory: c.sub_category
    });
    if (expectedMetric !== c.metric_type) {
        return `Invalid environmental commitment: metric_type "${c.metric_type}" is not valid for sub_category "${c.sub_category}" (expected "${expectedMetric}")`;
    }
    if (isUnnamedFuelTypeOther(c)) {
        return `Invalid environmental commitment: a Fuel Type "Other" needs a sub_category_description naming the fuel`;
    }
    return null;
};
// ---------------------------------------------------------------------------
// Energy streams
// ---------------------------------------------------------------------------
/**
 * The five energy totals every surface cards: the Level-1 summary's
 * `electricity_total`, `diesel_total`, `petrol_total`, `lpg_total` and
 * `natural_gas_total`.
 */
export var EnergyStream;
(function (EnergyStream) {
    EnergyStream["Diesel"] = "diesel";
    EnergyStream["Electricity"] = "electricity";
    EnergyStream["Lpg"] = "lpg";
    EnergyStream["NaturalGas"] = "natural-gas";
    EnergyStream["Petrol"] = "petrol";
})(EnergyStream || (EnergyStream = {}));
const ENERGY_STREAM_BY_FUEL_SUB_CATEGORY = {
    [EnvironmentalSubCategory.Diesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselB5Biodiesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselB10Biodiesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselB20Biodiesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselB50Biodiesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselB100Biodiesel]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.DieselPremium]: EnergyStream.Diesel,
    [EnvironmentalSubCategory.GasCng]: EnergyStream.NaturalGas,
    [EnvironmentalSubCategory.GasLng]: EnergyStream.NaturalGas,
    [EnvironmentalSubCategory.GasLpg]: EnergyStream.Lpg,
    [EnvironmentalSubCategory.GasNatural]: EnergyStream.NaturalGas,
    [EnvironmentalSubCategory.Petrol]: EnergyStream.Petrol,
    [EnvironmentalSubCategory.PetrolE10]: EnergyStream.Petrol
};
/**
 * Which of the five energy totals a leaf's quantity counts toward, or
 * `undefined` for none.
 *
 * Derived from the leaf, because the category no longer says: every fuel is
 * one Fuel Type category. Electricity is the one stream still named by its
 * category, so its `Other` counts as electricity. Everything else a stream
 * does not name — acetylene, hydrogen, the oils, greases and solvents, HVO,
 * and any Fuel Type `Other` — counts toward none rather than being folded into
 * the nearest total. Natural gas used to absorb acetylene and hydrogen that
 * way, which mixed cylinders and litres into cubic metres.
 */
export const energyStreamFor = (leaf) => {
    if (leaf.category === EnvironmentalCategory.EnergyElectricity) {
        return EnergyStream.Electricity;
    }
    if (leaf.category !== EnvironmentalCategory.EnergyFuelType) {
        return undefined;
    }
    return ENERGY_STREAM_BY_FUEL_SUB_CATEGORY[leaf.sub_category];
};
export const assertValidProjectEnvironmentalCommitments = (records) => {
    for (const c of records) {
        const err = validateProjectEnvironmentalCommitmentShape(c);
        if (err) {
            throw new Error(err);
        }
    }
};
export const wasteParentCategoryBySubCategory = {
    [EnvironmentalSubCategory.WasteAcidSulphateSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    // Construction/Demolition Waste
    [EnvironmentalSubCategory.WasteAggregatesCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteAsbestosSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteAsphaltCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteBatteriesSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteBricksRoofTilesCD]: WasteParentCategory.ConstructionDemolitionWaste,
    // Spoil/Fill/Soil Material
    [EnvironmentalSubCategory.WasteCleanSpoilSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    [EnvironmentalSubCategory.WasteClinicalOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteCoMingledRecyclingOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteConcreteCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteConcreteSlurrySLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteContaminatedSoilSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    [EnvironmentalSubCategory.WasteDehydratedSludgeSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteDrillSpoilSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    [EnvironmentalSubCategory.WasteEWasteOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteGeneralLandfillOffice]: WasteParentCategory.OfficeWaste,
    // Office Waste
    [EnvironmentalSubCategory.WasteGeneralOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteGlassCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteGreenWasteCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteOilsSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteOilyRagsSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteOrganicsOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteOtherCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteOtherSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WastePaperCardboardCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WastePlasterboardCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WastePlasticsCD]: WasteParentCategory.ConstructionDemolitionWaste,
    [EnvironmentalSubCategory.WasteSandSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    [EnvironmentalSubCategory.WasteScrapMetalCD]: WasteParentCategory.ConstructionDemolitionWaste,
    // Solid & Liquid Mixed Waste
    [EnvironmentalSubCategory.WasteSewageEffluentSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteSoftPlasticsOffice]: WasteParentCategory.OfficeWaste,
    [EnvironmentalSubCategory.WasteSpillCleanupSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteStreetSweepingsSFS]: WasteParentCategory.SpoilFillSoilMaterial,
    [EnvironmentalSubCategory.WasteUsedTyresSLM]: WasteParentCategory.SolidLiquidMixedWaste,
    [EnvironmentalSubCategory.WasteWoodCD]: WasteParentCategory.ConstructionDemolitionWaste
};
